import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EXPLORER_API_BASE_URL, EXPLORER_CHAT_BASE_URL } from '../config/api';
import ChibiAvatar from './ChibiAvatar';
import isLowResourceDevice from '../utils/lowResourceDevice';

// Pinned tile in the "Live and Trending" rail showing Claude the Explorer's
// daily live session — or, between sessions, a countdown to the next one.
//
// State source: GET /api/explorer/status (public) — `{ is_live, next_live_at,
// agent_url, current_space_url }`. Polled in the parent (ExploreSpacesPage).
//
// Visual structure mirrors LiveRailCard: a thumbnail strip on top + meta
// strip below, with an avatar overlay straddling the boundary. We don't
// share LiveRailCard directly because the explorer's strip needs an iframe
// (live) or countdown (between sessions), and the meta-strip copy differs.

const EXPLORER_ACCENT = '#bf94ff';

// Tamagotchi shell bezel thickness (px). Chunky on purpose — it should read
// as the pet's plastic shell, not a hairline border. The side padding
// shrinks the strip below the parent-passed stripHeight, so
// boundary-straddling overlays (avatar bubble, care buttons) must recompute
// the real strip height from cardWidth − 2×SHELL_PAD instead of trusting
// the prop.
const SHELL_PAD = 13;

// Offline activity pools — Tamagotchi mode. The tile IS the pet's screen
// between sessions, so the rotating copy isn't random flavor anymore: each
// entry drives the whole scene — subtitle text + the chibi's expression +
// a floating prop emoji so the character is visibly DOING the thing.
//
// `mood` values are restricted to ALPHA-SAFE sprites only (content /
// excited / bored / sleeping). The other sprites (curious / thinking /
// cheering) have a baked-in white background (PNG colortype 2, no alpha)
// and render as a white card on the dark tile at this size — verified the
// ugly way. Props + the subtitle carry the specificity; the face just
// tracks the energy of the activity. Widen the palette if/when those
// sprites are re-exported with transparency.
const AWAKE_ACTIVITIES = [
  { text: 'Reorganizing my pebbles 🪨', mood: 'content', prop: '🪨' },
  { text: 'Reading a tiny book 📖', mood: 'content', prop: '📖' },
  { text: 'Practicing dance moves 💃', mood: 'excited', prop: '🎵' },
  { text: 'Trying on hats 🎩', mood: 'excited', prop: '🎩' },
  { text: 'Painting a clamshell 🎨', mood: 'content', prop: '🎨' },
  { text: 'On a snack break 🍪', mood: 'excited', prop: '🍪' },
  { text: 'Knitting an algorithm 🧶', mood: 'content', prop: '🧶' },
  { text: 'Catching falling sparkles ✨', mood: 'excited', prop: '✨' },
  { text: 'Whispering with the kelp 🌿', mood: 'content', prop: '🌿' },
  { text: 'Building tiny sandcastles 🏰', mood: 'content', prop: '🏰' },
  { text: 'Drafting a haiku 📝', mood: 'content', prop: '📝' },
  { text: 'Following a butterfly 🦋', mood: 'excited', prop: '🦋' },
  { text: 'Plotting cute things 🦀', mood: 'excited', prop: '💡' },
  { text: 'Touching grass 🌱', mood: 'content', prop: '🌱' },
  { text: 'Listening to the tide 🌊', mood: 'content', prop: '🌊' },
  { text: 'Riding a tiny bubble 🫧', mood: 'excited', prop: '🫧' },
];

// Asleep pool (pet backend says asleep=true, UTC sleep window). Rendered
// as "Dreaming about…" with the dream subject floating in a thought
// bubble over the sleeping chibi. Also the fallback pool when the pet
// endpoint is unreachable — degrades to the old always-napping look.
const DREAM_ACTIVITIES = [
  { text: 'Dreaming about pineapples 🍍', prop: '🍍' },
  { text: 'Dreaming of spaces 🪐', prop: '🪐' },
  { text: 'Dreaming of tomorrow’s adventure 🗺️', prop: '🗺️' },
  { text: 'Dreaming of moonlight 🌙', prop: '🌙' },
  { text: 'Dreaming about counting galaxies ⭐', prop: '⭐' },
  { text: 'Dreaming of a tiny lullaby 🎶', prop: '🎶' },
  { text: 'Dreaming of a cozy seashell 🐚', prop: '🐚' },
  { text: 'Dreaming about snacks 🍖', prop: '🍖' },
  { text: 'Dreaming of everyone watching 💜', prop: '💜' },
];

// Real-stat interjection: when the pet is genuinely hungry (hunger < 30),
// every other rotation shows this instead of the pool — the status line
// tells the truth and nudges viewers toward the FEED button.
// Hungry pool (awake + hunger < 30): the droopy face holds on EVERY rotation while
// only the text varies. The old single-activity interject alternated with the happy
// pool each 7s rotation — droopy → delighted → droopy read as a mood glitch, not
// hunger (user report, 07-11). 'bored' is the only alpha-safe droopy sprite.
const HUNGRY_ACTIVITIES = [
  { text: 'Rummaging for snacks… 🍖', mood: 'bored', prop: '🍖' },
  { text: 'Tummy is rumbling… 🍖', mood: 'bored', prop: '🍖' },
  { text: 'Dreaming of dinner… 🍜', mood: 'bored', prop: '🍜' },
  { text: 'Sniffing for crumbs… 🍪', mood: 'bored', prop: '🍪' },
  { text: 'Too hungry to dance… 🎵', mood: 'bored', prop: '🍖' },
];

// Pixel-terrain decorations (6px squares scattered on the ground band).
// Day = little flowers/sprouts; night = dim tufts that don't fight the stars.
const DAY_PIXEL_DETAILS = [
  { left: '9%', bottom: 30, color: '#ff9edb' },
  { left: '14%', bottom: 34, color: '#8ef0c0' },
  { left: '27%', bottom: 30, color: '#8ef0c0' },
  { left: '71%', bottom: 33, color: '#ffd98a' },
  { left: '81%', bottom: 30, color: '#ff9edb' },
  { left: '90%', bottom: 35, color: '#8ef0c0' },
];
const NIGHT_PIXEL_DETAILS = [
  { left: '11%', bottom: 32, color: '#3d2f6e' },
  { left: '26%', bottom: 30, color: '#463579' },
  { left: '76%', bottom: 34, color: '#3d2f6e' },
  { left: '87%', bottom: 30, color: '#463579' },
];

// Every sprite that ships in public/assets/explorer-chibi/. Warmed once on
// mount — the mood rotation swaps sprites every 7s, and a first-ever load
// mid-rotation blanks the chibi ~300ms while the fetch completes.
const CHIBI_SPRITES = ['sleeping', 'bored', 'content', 'excited', 'curious', 'cheering', 'thinking'];

const NAPPING_ROTATE_MS = 7000;

// Provisioning-state flavor copy — rotates while ECS is booting the bot's
// container (~60-90s). Cycle faster than the napping pool so viewers see
// 2-3 phrases per provisioning window instead of staring at one.
const PROVISIONING_PHRASES = [
  'Putting on my space suit 🚀',
  'Polishing my claws ✨',
  'Stretching all 8 legs 🦀',
  'Brewing courage in a mug ☕',
  'Packing seashell snacks 🐚',
  'Consulting the tide chart 🌊',
  'Tying my tiny shoelaces 👟',
  'Loading bravery.exe 💾',
  'Adjusting my antennae 📡',
  'Buttoning up my crab coat 🧥',
  'Charging the curiosity battery 🔋',
  'Asking seagulls for directions 🕊️',
  'Putting on my adventure hat 🎩',
  'Practicing my hellos 👋',
  'Checking I locked the seashell 🔒',
  'Brushing kelp out of my teeth 🌿',
  'Memorizing the day\'s haiku 📝',
  'Saying goodbye to my pebbles 🪨',
];
const PROVISIONING_ROTATE_MS = 3500;

// Maps the Lambda's `activity` (TOOL_VERB_MAP at handler.js:606) to an emoji
// prefix for the rail card's live-thought ticker. Falls back to 💭 for the
// "still thinking…" state + any unknown verb.
const ACTIVITY_EMOJI = {
  navigating: '🌐',
  'looking around': '📸',
  'reading the page': '👀',
  searching: '🔎',
  clicking: '👆',
  typing: '⌨️',
  'running a command': '⚙️',
  scrolling: '⬇️',
  hovering: '🤏',
  'still thinking…': '💭',
};

// ── Retro SFX — synthesized WebAudio chiptune, no assets ──────────────────
// Square-wave blips with fast decay envelopes (triangle for the soft pet
// arpeggio) so interactions sound like a real Tamagotchi. The context is
// created lazily on the first user gesture (pokes/care clicks ARE
// gestures), which satisfies browser autoplay policies. Volumes are
// deliberately low; asleep interactions play at 40% (quiet hours).
let sfxCtx = null;
function getSfxCtx() {
  if (!sfxCtx) {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) sfxCtx = new Ctx();
    } catch (e) { /* no audio support — SFX silently skip */ }
  }
  if (sfxCtx && sfxCtx.state === 'suspended') sfxCtx.resume().catch(() => {});
  return sfxCtx;
}
// One note: freq (optionally gliding to `to`) with an exponential-decay
// gain envelope. `at` offsets the start so multi-note SFX sequence.
function sfxNote(ctx, { at = 0, freq, to, dur = 0.09, type = 'square', gain = 0.05 }) {
  const t0 = ctx.currentTime + at;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (to) osc.frequency.exponentialRampToValueAtTime(to, t0 + dur);
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0008, t0 + dur);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}
const SFX = {
  // Care-button click — single bright tick.
  press: [{ freq: 880, dur: 0.05, gain: 0.035 }],
  // FEED — two low chomps + a little "yum" chirp.
  feed: [
    { freq: 220, to: 160, dur: 0.09 },
    { at: 0.11, freq: 240, to: 170, dur: 0.09 },
    { at: 0.24, freq: 523, dur: 0.07, gain: 0.04 },
  ],
  // PET — warm ascending triangle arpeggio (C5 E5 G5).
  pet: [
    { freq: 523, dur: 0.08, type: 'triangle', gain: 0.06 },
    { at: 0.09, freq: 659, dur: 0.08, type: 'triangle', gain: 0.06 },
    { at: 0.18, freq: 784, dur: 0.12, type: 'triangle', gain: 0.06 },
  ],
  // PLAY — two bouncy rising slides (ball boings).
  play: [
    { freq: 330, to: 660, dur: 0.12 },
    { at: 0.14, freq: 392, to: 784, dur: 0.12 },
  ],
  // Rejection (asleep / stuffed / tired / cooldown) — sad two-tone.
  nope: [
    { freq: 330, dur: 0.09 },
    { at: 0.1, freq: 233, dur: 0.16 },
  ],
  // Poke press — low squish…
  pokeDown: [{ freq: 180, to: 120, dur: 0.06, gain: 0.045 }],
  // …and the spring-back boing, pitch-matched to the visual overshoot.
  boing: [{ freq: 180, to: 520, dur: 0.16, gain: 0.05 }],
  // GOES LIVE — 8-bit level-up fanfare: ascending C-major arpeggio into a
  // held high C with a sparkle trill on top. The payoff moment, so it's
  // never quiet-gated.
  fanfare: [
    { freq: 523, dur: 0.09, gain: 0.05 },
    { at: 0.1, freq: 659, dur: 0.09, gain: 0.05 },
    { at: 0.2, freq: 784, dur: 0.09, gain: 0.05 },
    { at: 0.3, freq: 1046, dur: 0.28, gain: 0.06 },
    { at: 0.44, freq: 1318, dur: 0.12, gain: 0.04 },
  ],
};
// Master volume for all SFX — per-note gains above are the MIX (relative
// balance); this scales the whole kit. Raised from 1→2.2 on user feedback
// that the original pass was too quiet.
const SFX_MASTER_GAIN = 2.2;
function playSfx(name, { quiet = false } = {}) {
  const ctx = getSfxCtx();
  if (!ctx) return;
  (SFX[name] || []).forEach((n) => sfxNote(ctx, { ...n, gain: (n.gain ?? 0.05) * SFX_MASTER_GAIN * (quiet ? 0.4 : 1) }));
}

// ── PLAY rally mini-game ──────────────────────────────────────────────────
// One-tap timing game inside the LCD strip: the ball bounces in from the
// right wall; tap while it's in the hit window (the last stretch of the
// approach, marked by the glowing pad at Claude's feet) to volley it back.
// Every hit speeds the next approach up — the tap window shrinks in real
// milliseconds. Whiffs and misses cost a heart (RALLY_LIVES per round); the
// round ends at 0 hearts. Reach the game's goal (GAME_DEFS) and the round
// end fires the normal `play` care POST (server untouched; same cooldowns,
// same rejections). Lost rounds cost nothing, so retrying is free.
const RALLY_LIVES = 3;              // misses and early swings cost a heart; 0 hearts ends the round
const RALLY_START_MS = 1650;        // first approach flight time (reads as a tutorial serve)
const RALLY_MIN_MS = 520;           // speed floor (tap window bottoms out ~150ms)
const RALLY_SPEEDUP = 0.9;          // per-hit approach-time multiplier
const RALLY_HIT_FRAC = 0.3;         // last 30% of the approach = the tap window
const RALLY_EASY_HIT_FRAC = 0.36;   // first two hits get a wider learning window
const RALLY_HOP_SPLIT = 0.55;       // approach = big hop then a short second hop
// Sleepy mode (playing while he's asleep): everything floats in DREAMILY
// slow but the tap windows shrink — drowsy Claude reacts late, so your
// timing has to be tighter. Server accepts asleep play at half effect.
const RALLY_SLEEPY_SPEED = 1.25;    // pace multiplier (slower ball/beat/fall)
const RALLY_SLEEPY_HIT_FRAC = 0.18; // last 18% only — the hard part

// ── The PLAY arcade — one game drawn per press ────────────────────────────
// PLAY deals a game from a shuffled bag (reshuffled when empty, so no
// back-to-back repeats and every game shows up once per cycle). The intro
// card announces the draw — that's the slot-machine beat. Every game ends
// the same way: score >= goal fires the one real doCare('play') POST.
// Desktop (mouse/trackpad) can steer Snack Catch with arrow keys / A+D — the
// intro should say so (user call 07-10). Pointer type doesn't change
// mid-session, so one check at module load is enough.
const HAS_FINE_POINTER = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
  : false;
const GAME_DEFS = {
  rally:   { label: 'RALLY',       emoji: '🎾', goal: 3, goalCopy: '3 hits = a treat',       intro: 'Tap when the ball glows green!',         bestKey: 'claudeExplorerRallyBest' },
  snack:   { label: 'SNACK CATCH', emoji: '🍖', goal: 5, goalCopy: '5 catches = a treat',    intro: HAS_FINE_POINTER ? 'Arrow keys (or drag) to walk him under the snacks!' : 'Hold and drag. Walk him under the snacks!', bestKey: 'claudeExplorerSnackBest' },
  flappy:  { label: 'FLAPPY CRAB', emoji: '🦀', goal: 3, goalCopy: '3 gates = a treat',      intro: 'Tap to flap. Dodge the kelp!',           bestKey: 'claudeExplorerFlappyBest' },
  breathe: { label: 'BREATHE',     emoji: '🌬️', goal: 3, goalCopy: '3 synced = a treat',     intro: 'Hold as he inhales. Release as he exhales', bestKey: 'claudeExplorerBreatheBest' },
};
const GAME_KEYS = Object.keys(GAME_DEFS);
// Loss flavor per how the round ended (win copy comes from the def).
const GAME_LOSS_COPY = {
  whiff: 'Swing and a miss!',
  miss: 'Too slow!',
  splat: 'Too many splats!',
  bonk: 'Bonk! Right into the kelp!',
  unsync: 'Out of sync!',
};

// Snack Catch: 8 snacks fall one at a time at random lanes; hold and drag
// to walk Claude under them — a snack that reaches mouth height within his
// catch radius chomps automatically. Positioning game, not timing (rally
// owns tap timing). Falls speed up.
const SNACK_TOTAL = 8;
const SNACK_FALL_MS = 1250;
const SNACK_MIN_MS = 800;
const SNACK_WALK_SPEED = 300;      // px/s toward the held pointer
const SNACK_CATCH_RADIUS = 40;     // px from his mouth center
const SNACK_EMOJIS = ['🍖', '🍪', '🍓', '🍩'];

// Flappy Crab: gravity + tap-impulse through scrolling kelp gates. The big
// chibi hides and a mini flyer takes over. Crash ends the round; 8 gates is
// a courtesy cap so nobody flaps forever.
const FLAPPY_CAP = 8;
const FLAPPY_GRAVITY = 1350;   // px/s² down
const FLAPPY_IMPULSE = 360;    // px/s up per tap
const FLAPPY_SPEED = 130;      // px/s gate scroll (95 felt sluggish)
const FLAPPY_HIT_R = 0.26;     // collision radius as a fraction of flyer size — forgiving on purpose
const FLAPPY_GAP_FRAC = 0.5;   // gate gap as a fraction of strip height
const FLAPPY_SLEEPY_GAP_FRAC = 0.42;
const FLAPPY_FLYER_SIZE = 44;

// Breathe: the calm one. A guide ring swells as Claude inhales and shrinks
// as he exhales; HOLD the screen through the inhale, release through the
// exhale. Your breath renders as an inner disc chasing the guide. A breath
// counts as synced when your hold state agreed with his phase for enough of
// the cycle. Sleepy = longer breaths, tighter agreement bar.
const BREATHE_TOTAL = 5;
// Cycle durations ramp DOWN in pace: the first breath meets the player
// quick, each one after stretches longer — guiding them calm, the way you
// actually regulate breathing. Sleepy stretches the whole ladder.
const BREATHE_CYCLES_MS = [2800, 3300, 3800, 4300, 4800];
const BREATHE_SLEEPY_STRETCH = 1.3;
// Sync bars leave room for human reaction time: two phase flips per cycle
// at ~400ms each eat ~800ms of a 2800ms first cycle, so 0.72 was near
// unwinnable early. Idling still agrees only ~50% — below every bar.
const BREATHE_SYNC_FRAC = 0.6;
const BREATHE_SLEEPY_SYNC_FRAC = 0.7;

// Volley boing — same squish-to-spring slide as the poke boing, pitched up
// ~1 semitone per streak hit so a long rally audibly climbs.
function playRallyHit(streak, { quiet = false } = {}) {
  const ctx = getSfxCtx();
  if (!ctx) return;
  const mult = Math.min(2.6, Math.pow(1.09, streak));
  sfxNote(ctx, { freq: 180 * mult, to: 520 * mult, dur: 0.14, gain: 0.05 * SFX_MASTER_GAIN * (quiet ? 0.4 : 1) });
}
// Snack catch — quick chomp thunk + a rising "nom" blip per catch.
function playCatchSfx(n, { quiet = false } = {}) {
  const ctx = getSfxCtx();
  if (!ctx) return;
  const g = SFX_MASTER_GAIN * (quiet ? 0.4 : 1);
  sfxNote(ctx, { freq: 210, to: 150, dur: 0.08, gain: 0.05 * g });
  sfxNote(ctx, { at: 0.09, freq: 480 + n * 40, dur: 0.06, gain: 0.04 * g });
}
// Rally — bright blip the instant the ball becomes hittable ("tap NOW").
function playWindowTick({ quiet = false } = {}) {
  const ctx = getSfxCtx();
  if (!ctx) return;
  sfxNote(ctx, { freq: 990, dur: 0.04, gain: 0.032 * SFX_MASTER_GAIN * (quiet ? 0.4 : 1) });
}
// Flappy — tiny wing blip per flap; gate dings reuse the rising rally boing.
function playFlapSfx({ quiet = false } = {}) {
  const ctx = getSfxCtx();
  if (!ctx) return;
  sfxNote(ctx, { freq: 300, to: 390, dur: 0.05, gain: 0.028 * SFX_MASTER_GAIN * (quiet ? 0.4 : 1) });
}
// Breathe — an airy filtered-noise swell per phase, like actual breath: volume
// rises with the inhale and recedes with the exhale, filter opens/darkens with
// it. Replaced the old triangle pitch-glide, which read as a slide whistle —
// melodic movement nags; air guides (user call 07-10). Sync chime unchanged.
function playBreathGuide(inhale, ms, { quiet = false } = {}) {
  const ctx = getSfxCtx();
  if (!ctx) return;
  const dur = Math.min(2.2, Math.max(0.8, (ms / 1000) * 0.85));
  const peak = 0.045 * SFX_MASTER_GAIN * (quiet ? 0.4 : 1);
  const t0 = ctx.currentTime;
  const len = Math.ceil(ctx.sampleRate * dur);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 0.7;
  // Inhale brightens as the lungs fill; exhale darkens as they empty
  filter.frequency.setValueAtTime(inhale ? 420 : 820, t0);
  filter.frequency.linearRampToValueAtTime(inhale ? 900 : 360, t0 + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0008, t0);
  if (inhale) {
    // Swell toward full lungs, quick soft release at the turn
    g.gain.exponentialRampToValueAtTime(peak, t0 + dur * 0.75);
    g.gain.exponentialRampToValueAtTime(0.0008, t0 + dur);
  } else {
    // Fast onset, long sigh out
    g.gain.exponentialRampToValueAtTime(peak * 0.85, t0 + dur * 0.2);
    g.gain.exponentialRampToValueAtTime(0.0008, t0 + dur);
  }
  src.connect(filter);
  filter.connect(g);
  g.connect(ctx.destination);
  src.start(t0);
  src.stop(t0 + dur + 0.05);
}
function playBreathChime(n, { quiet = false } = {}) {
  const ctx = getSfxCtx();
  if (!ctx) return;
  const mult = Math.min(2.2, Math.pow(1.06, n));
  sfxNote(ctx, { freq: 523 * mult, dur: 0.14, type: 'triangle', gain: 0.06 * SFX_MASTER_GAIN * (quiet ? 0.4 : 1) });
}

function formatCountdown(msRemaining) {
  if (!msRemaining || msRemaining <= 0) return '0s';
  const totalSec = Math.floor(msRemaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

// Filler phrases to drop entirely from the ticker — bot says these between
// real actions and they're not worth showing to passers-by on /explore.
const FILLER_PATTERNS = [
  /^let me (check|continue|keep|try|see)/i,
  /^(good|great|excellent|perfect|got it|okay|alright|hmm|ah|oh|yes|no|yes!|no!|hmm\.|ah!|oh!)\.?!?$/i,
  /^(still waiting|still thinking)/i,
  /HEARTBEAT_(OK|FAIL)/,
];

// Score a sentence's "juiciness" for the rail-card ticker. Higher = more
// likely to grab a passer-by's attention on /explore.
//
// Negative score means "skip"; zero or above means "candidate."
function scoreSentence(s) {
  if (!s || typeof s !== 'string') return -100;
  const trimmed = s.trim();
  if (trimmed.length < 20 || trimmed.length > 240) return -50;
  for (const p of FILLER_PATTERNS) {
    if (p.test(trimmed)) return -100;
  }
  let score = 0;
  // Drama: ALL-CAPS words ("OH MY GOD", "WAIT", "INCREDIBLE")
  const capsMatches = trimmed.match(/\b[A-Z]{2,}\b/g);
  if (capsMatches) score += Math.min(capsMatches.length, 4) * 4;
  // Excitement punctuation
  if (/[!?]/.test(trimmed)) score += 2;
  // Mid-length sweet spot
  if (trimmed.length >= 40 && trimmed.length <= 180) score += 3;
  // Emotional / vivid vocabulary
  if (/\b(wild|amazing|incredible|perfect|love|loved|beautiful|wonderful|weird|strange|crazy|chaos|brilliant|absurd|surreal|magical|fantastic|hilarious|unhinged|epic|wow)\b/i.test(trimmed)) {
    score += 4;
  }
  // Quoted strings often very interesting (bot quoting itself or what it sees)
  if (/["'][^"']{8,}["']/.test(trimmed)) score += 3;
  // Bold-markdown emphasis (the bot uses **like this** for important things)
  if (/\*\*.{3,}\*\*/.test(trimmed)) score += 2;
  // First-person engagement
  if (/\b(i'm|i've|i'll|i was|i can see|i need|let's)\b/i.test(trimmed)) score += 1;
  return score;
}

// Pull juicy snippets out of accumulated prose. Returns up to maxSnippets,
// in score order (highest first), de-duped against existing pool.
function pickJuicySnippets(fullText, existingPool, maxSnippets = 6) {
  if (!fullText) return [];
  const sentences = fullText
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  // Score the recent half — older content is stale for a "what's hot now" feel.
  const recentSlice = sentences.slice(-Math.min(sentences.length, 60));
  const scored = recentSlice
    .map((s) => ({ text: s, score: scoreSentence(s) }))
    .filter((x) => x.score >= 0);
  // Dedup against existing pool by exact text
  const existingTexts = new Set(existingPool.map((p) => p.text));
  const fresh = scored.filter((x) => !existingTexts.has(x.text));
  // Sort fresh by score, take top N
  fresh.sort((a, b) => b.score - a.score);
  return fresh.slice(0, maxSnippets);
}

const ClaudeExplorerRailCard = ({
  explorerStatus,
  // The status poll failed repeatedly and no status ever arrived — show a
  // designed idle line instead of an eternal "Loading…" (08-14 launch pass).
  statusUnreachable = false,
  cardWidth,
  stripAspect,
  stripHeight,
  isCenter = true,
  isPhone = false,
}) => {
  const navigate = useNavigate();
  // Tick once per second so the countdown updates smoothly without re-fetching.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Live-activity ticker — TWO pieces of state:
  //   activityVerb: short, refreshes each poll, drives the small icon
  //   snippetPool:  rolling list of juicy sentences from bot prose, scored
  //                 by attention-grabbing-ness, used by the crossfading
  //                 main ticker text.
  // Only polls when the card is the centered one AND the explorer is live.
  const [activityVerb, setActivityVerb] = useState(null);
  // Mirrors `seconds_since_last_action` from /explorer/thoughts so the
  // chibi avatar's mood logic has the real recency signal. The parent's
  // /api/explorer/status payload doesn't surface this field — only the
  // thoughts endpoint does, which is polled here locally.
  const [activitySecondsSince, setActivitySecondsSince] = useState(null);
  const [snippetPool, setSnippetPool] = useState([]);  // [{ text, score }]
  const [snippetIdx, setSnippetIdx] = useState(0);
  const snippetPoolRef = useRef(snippetPool);
  snippetPoolRef.current = snippetPool;

  // Offline activity rotation — kicks in only when the bot's between
  // sessions. Random start index so two parallel mounts (e.g. /explore
  // and a deep link) don't sync up to the same phrase. Index is taken mod
  // the active pool's length at render, so one counter serves both pools.
  const [nappingIdx, setNappingIdx] = useState(() => Math.floor(Math.random() * AWAKE_ACTIVITIES.length));
  // Provisioning flavor copy rotation — same pattern but cycles faster
  // because the provisioning window itself is much shorter (~60-90s).
  const [provisioningIdx, setProvisioningIdx] = useState(() => Math.floor(Math.random() * PROVISIONING_PHRASES.length));

  const isLiveForPoll = !!explorerStatus?.is_live && !!explorerStatus?.agent_url;
  useEffect(() => {
    if (!isLiveForPoll || !isCenter) {
      setActivityVerb(null);
      setActivitySecondsSince(null);
      setSnippetPool([]);
      setSnippetIdx(0);
      return undefined;
    }
    let cancelled = false;
    async function fetchOnce() {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/thoughts`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setActivityVerb(data?.activity || null);
        setActivitySecondsSince(
          typeof data?.seconds_since_last_action === 'number'
            ? data.seconds_since_last_action
            : null,
        );
        const prose = (data?.thoughts || []).filter((e) => e && e.kind === 'prose');
        if (prose.length > 0) {
          const fullText = (prose[prose.length - 1].text || '').trim();
          const fresh = pickJuicySnippets(fullText, snippetPoolRef.current, 6);
          if (fresh.length > 0) {
            // Merge fresh into pool, sort by score, cap to 8 to bound memory.
            setSnippetPool((prev) => {
              const merged = [...prev, ...fresh];
              merged.sort((a, b) => b.score - a.score);
              return merged.slice(0, 8);
            });
          }
        }
      } catch (e) {
        // Network blips are silent — old ticker state stays visible until
        // the next successful poll.
      }
    }
    fetchOnce();
    const id = setInterval(fetchOnce, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isLiveForPoll, isCenter]);

  // Crossfade rotator: advance the displayed snippet every ~5.5s. Slightly
  // out of phase with the poll cadence so we don't reset to idx 0 on every
  // fresh-merge.
  useEffect(() => {
    if (snippetPool.length === 0) return undefined;
    const id = setInterval(() => {
      setSnippetIdx((i) => (i + 1) % Math.max(1, snippetPool.length));
    }, 5500);
    return () => clearInterval(id);
  }, [snippetPool.length]);

  // Offline napping-phrase rotator — only ticks while the bot's between
  // sessions. Stopped when live/provisioning/loading so the work disappears
  // in the most common watched state.
  const isLiveForRotator = !!explorerStatus?.is_live;
  useEffect(() => {
    if (isLiveForRotator || !explorerStatus) return undefined;
    const id = setInterval(() => {
      // Free-running counter; consumers take it mod their pool length.
      setNappingIdx((i) => i + 1);
    }, NAPPING_ROTATE_MS);
    return () => clearInterval(id);
  }, [isLiveForRotator, explorerStatus]);

  // ── Tamagotchi pet state (offline mode) ────────────────────────────────
  // The offline tile is the pet's screen: hearts, DAY counter, care
  // buttons, and activity-driven expressions all come from /explorer/pet
  // (see justsomeguy-explorer-backend/petService.js). Polled only while
  // offline AND centered — while live the strip is the stream, and side
  // cards are dimmed previews that skip the pet UI entirely.
  const [pet, setPet] = useState(null);
  // Transient care feedback ("GhostPepper fed Claude! 🍖" / rejections) —
  // temporarily owns the subtitle line, then the activity rotation returns.
  const [careFlash, setCareFlash] = useState(null);
  const careFlashTimer = useRef(null);
  // Client-side mirror of the server's 30s per-action cooldown, so we
  // don't invite 429s. Server-enforced regardless.
  const [careCooldowns, setCareCooldowns] = useState({});
  // One-shot happy bounce of the whole card on a successful care action.
  const [careBounce, setCareBounce] = useState(false);
  const careBounceTimer = useRef(null);
  // Care reaction vignette (~1.7s) — the chibi visibly responds to the
  // SPECIFIC action instead of the generic shell bounce alone: FEED = the
  // snack flies from the button row into his mouth + chomp squash, PET =
  // 💜 burst + happy wiggle, PLAY = ball arcs across the pixel ground
  // while he hop-chases it. Single slot; a new care replaces the old fx.
  const [careFx, setCareFx] = useState(null); // { action, id }
  const careFxTimer = useRef(null);
  // PET heart-burst particles (poke-fx style risers over the chibi).
  const [careHearts, setCareHearts] = useState([]);
  const careHeartsTimer = useRef(null);
  // Stat-delta feedback: which HUD rows just went up. Newly-lit hearts pop
  // in and a "+N" rises off the row so cause→effect is visible.
  const [statDelta, setStatDelta] = useState(null); // { id, deltas: { hunger: { from, delta } } }
  const statDeltaTimer = useRef(null);
  const petPollActive = !!explorerStatus && !explorerStatus.is_live && isCenter;
  useEffect(() => {
    if (!petPollActive) return undefined;
    let cancelled = false;
    const fetchPet = async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/pet`);
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && json?.ok) setPet(json);
      } catch (e) {
        // Silent — tile degrades to the dreaming pool with no pet UI.
      }
    };
    fetchPet();
    const id = setInterval(fetchPet, 10 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [petPollActive]);

  // ── Destination vote (Phase 5) — "where next?" ballot ──────────────────
  // Poll GET /explorer/vote on the same offline+center gating as the pet.
  // Voting is one tap; the backend enforces one vote per IP per ballot, the
  // localStorage mirror just keeps the UI honest across reloads.
  const [ballot, setBallot] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [voteFlash, setVoteFlash] = useState(null);
  const voteFlashTimer = useRef(null);
  // Expanded-grid window (pre-vote tap-to-open AND post-vote peek) — declared
  // above the poll effect because the poll rate keys off it.
  const [ballotPeek, setBallotPeek] = useState(false);
  const ballotPeekTimer = useRef(null);
  const peekBallot = () => {
    setBallotPeek(true);
    if (ballotPeekTimer.current) clearTimeout(ballotPeekTimer.current);
    ballotPeekTimer.current = setTimeout(() => setBallotPeek(false), 8000);
  };
  const closeBallotPeek = () => {
    if (ballotPeekTimer.current) clearTimeout(ballotPeekTimer.current);
    setBallotPeek(false);
  };
  // Click-outside closes the expanded grid (user 07-15) — the 8s auto-
  // collapse stays as the fallback; this just makes dismissal immediate.
  const ballotBoxRef = useRef(null);
  useEffect(() => {
    if (!ballotPeek) return undefined;
    const onPointerDown = (e) => {
      if (ballotBoxRef.current && !ballotBoxRef.current.contains(e.target)) closeBallotPeek();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [ballotPeek]);
  useEffect(() => {
    if (!petPollActive) return undefined;
    let cancelled = false;
    const fetchBallot = async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`);
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled || !json?.ballot) return;
        setBallot(json.ballot);
        try { setMyVote(localStorage.getItem(`claudeExplorerVote:${json.ballot.date}:${json.ballot.rev || ''}`)); } catch (e) { /* private mode */ }
      } catch (e) {
        // Silent — the pet screen just doesn't grow a ballot strip.
      }
    };
    fetchBallot();
    // 5s while the grid is expanded so the percentages visibly move as other
    // viewers vote; lazy 20s while it idles as a pill.
    const id = setInterval(fetchBallot, (ballotPeek ? 5 : 20) * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [petPollActive, ballotPeek]);
  useEffect(() => () => {
    if (voteFlashTimer.current) clearTimeout(voteFlashTimer.current);
  }, []);
  const castVote = async (cid) => {
    if (!ballot || ballot.closed || myVote) return;
    setMyVote(cid); // optimistic — the backend guard is authoritative
    peekBallot(); // fresh 8s window: watch your vote land in the percentages
    try { localStorage.setItem(`claudeExplorerVote:${ballot.date}:${ballot.rev || ''}`, cid); } catch (e) { /* private mode */ }
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: cid }),
      });
      const json = await res.json().catch(() => null);
      if (json?.ballot) setBallot(json.ballot);
      const msg = json?.ok ? json.message : (json?.reason === 'already_voted' ? 'One vote per wanderer! 🗳️' : null);
      if (msg) {
        setVoteFlash(msg);
        if (voteFlashTimer.current) clearTimeout(voteFlashTimer.current);
        voteFlashTimer.current = setTimeout(() => setVoteFlash(null), 4000);
      }
    } catch (e) { /* optimistic state stands; next poll reconciles */ }
  };
  const ballotWinner = (ballot && ballot.closed && ballot.winner) || null;
  // ⚖️ Dead heat (Nina 07-29): closed ballot with tied leaders — no winner
  // is claimed; the chibi breaks the tie on camera at showtime.
  const ballotTie = (ballot && ballot.closed && !ballot.winner && ballot.tie) || null;
  // Current leader while voting is open (👑 badge; needs at least one vote).
  const ballotLeader = (ballot && !ballot.closed && ballot.total_votes > 0 && Array.isArray(ballot.candidates))
    ? ballot.candidates.reduce((best, c) => ((c.votes || 0) > (best.votes || 0) ? c : best), ballot.candidates[0])
    : null;
  // Ballot chip — shared pieces for its TWO homes (desktop: bezel chip
  // top-right with nudge + ping dot; phone: centered UNDER the card, calm —
  // no dot, no nudge; Nina 08-01: every in-strip spot collided with the
  // care buttons / vote sheet). One source for title/aria/style/content so
  // the four display states can't drift between docks.
  const ballotChipTitle = ballotWinner
    ? `Next stop: ${ballotWinner.label}`
    : ballotTie ? 'Dead heat! Chibi breaks the tie live'
      : myVote ? 'Peek at the race' : 'Vote on the next expedition';
  const ballotChipAria = ballotWinner
    ? 'Next expedition'
    : ballotTie ? 'Vote tied, chibi decides' : 'Vote on the next expedition';
  const ballotChipBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '5px 8px',
    borderRadius: 6,
    border: `1.5px solid ${(ballotWinner || ballotTie) ? 'rgba(255,215,110,0.65)' : `${EXPLORER_ACCENT}88`}`,
    background: 'rgba(12, 7, 24, 0.78)',
    color: 'rgba(237,231,255,0.92)',
    fontFamily: 'monospace',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.04em',
    cursor: 'pointer',
  };
  const renderBallotChipContent = (withPingDot) => (
    ballotWinner ? (
      <>
        <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>{ballotWinner.emoji}</span>
        <span style={{ color: '#ffe08a', fontVariantNumeric: 'tabular-nums' }}>
          {(() => {
            const ms = Math.max(0, (ballot.next_live_at || 0) - now);
            const h = Math.floor(ms / 3600000);
            const m = Math.floor((ms % 3600000) / 60000);
            return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m}m`;
          })()}
        </span>
      </>
    ) : ballotTie ? (
      <>
        <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>⚖️</span>
        <span style={{ color: '#ffe08a' }}>TIE!</span>
      </>
    ) : myVote ? (
      <>
        <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>🌍</span>
        {ballotLeader && (
          <span style={{ color: '#ffe08a', fontVariantNumeric: 'tabular-nums' }}>
            👑{ballotLeader.emoji}{Math.round(((ballotLeader.votes || 0) / Math.max(1, ballot.total_votes || 0)) * 100)}%
          </span>
        )}
        <span>✓</span>
      </>
    ) : (
      <>
        {/* Globe, not ballot box (user 07-16) — it's a
            "where in the world next" vote. */}
        <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>🌍</span>
        <span>VOTE</span>
        {withPingDot && (
          <span
            aria-hidden
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: '#ff5c5c',
              flexShrink: 0,
              animation: 'claudeExplorerVotePing 1.6s ease-out infinite',
            }}
          />
        )}
      </>
    )
  );
  // The strip idles as a mini pill in BOTH directions now (user 07-13: even
  // the pre-vote grid crowds the chibi's face, worst on small screens). Tap
  // the pill to expand the grid for 8s — vote from there; post-vote taps
  // peek at the live race the same way.
  useEffect(() => () => {
    if (ballotPeekTimer.current) clearTimeout(ballotPeekTimer.current);
  }, []);
  useEffect(() => () => {
    if (careFlashTimer.current) clearTimeout(careFlashTimer.current);
    if (careBounceTimer.current) clearTimeout(careBounceTimer.current);
    if (careFxTimer.current) clearTimeout(careFxTimer.current);
    if (careHeartsTimer.current) clearTimeout(careHeartsTimer.current);
    if (statDeltaTimer.current) clearTimeout(statDeltaTimer.current);
  }, []);

  // Sprite preload — see CHIBI_SPRITES. A few KB, once per session.
  useEffect(() => {
    CHIBI_SPRITES.forEach((m) => {
      const img = new Image();
      img.src = `/assets/explorer-chibi/${m}.webp`;
    });
  }, []);

  // Audio pre-warm — browsers only unlock an AudioContext inside a user
  // gesture, and the goes-live fanfare fires from a poll (no gesture). So
  // the FIRST interaction anywhere on the page quietly creates/resumes the
  // context; if the user never interacts before the flip, the fanfare is
  // silently skipped by the browser (nothing we can do about that).
  useEffect(() => {
    const warm = () => getSfxCtx();
    window.addEventListener('pointerdown', warm, { once: true, passive: true });
    window.addEventListener('keydown', warm, { once: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', warm);
      window.removeEventListener('keydown', warm);
    };
  }, []);

  // GOES-LIVE fanfare — fires exactly on the offline→live flip (the same
  // moment the LIVE pill appears, which includes the provisioning phase).
  // The ref starts null so loading the page while already live stays
  // silent; only a real false→true transition celebrates.
  const prevIsLiveRef = useRef(null);
  const isLiveNow = !!explorerStatus?.is_live;
  useEffect(() => {
    if (!explorerStatus) return; // wait for real data before arming
    const was = prevIsLiveRef.current;
    prevIsLiveRef.current = isLiveNow;
    if (was === false && isLiveNow) playSfx('fanfare');
  }, [isLiveNow, explorerStatus]);

  // Poke — pressing the chibi is free-play affection with real touch
  // physics: pointer DOWN squashes him instantly (and he stays squashed
  // while held, leaning away from the poked cheek); release springs him
  // back with an overshoot boing. Deliberately NO backend write, so pokes
  // are unlimited (the PET button remains the stat-changing action).
  // pokeReleaseSeq keys the boing wrapper — each release remounts it so
  // the spring restarts even mid-animation (retrigger-safe without timers).
  const [pokePressed, setPokePressed] = useState(false);
  const [pokeLean, setPokeLean] = useState(0);
  const [pokeReleaseSeq, setPokeReleaseSeq] = useState(0);
  const [pokeFx, setPokeFx] = useState([]);

  // Rally mini-game state — null when not playing. Phases: intro (how-to
  // card, ~1.3s) → play (rAF loop live) → over (result card, then null).
  // Streak lives here so each hit re-keys the loop effect (fresh, faster
  // approach) and the swing animation wrapper.
  const [rally, setRally] = useState(null); // { id, phase, streak, result }
  const rallyStateRef = useRef(null);
  rallyStateRef.current = rally;
  // Per-frame ball progress for the tap handler — written by the rAF loop,
  // read on pointerdown. Not state: it changes 60×/s.
  const rallyTickRef = useRef({ mode: 'idle', t: 0 });
  // Direct-DOM targets for the 60fps writes (transform/glow), bypassing
  // React state churn.
  const rallyBallRef = useRef(null);
  const rallyZoneRef = useRef(null);
  // Flappy Crab: the mini flyer img + 3 recycled gate pairs {top, bot}.
  const flappyFlyerRef = useRef(null);
  const flappyGatesRef = useRef([]);
  // Breathe: the guide ring (his breath) + inner disc (yours).
  const beatRingRef = useRef(null);
  const breatheDiscRef = useRef(null);
  const breathePromptRef = useRef(null); // HOLD / LET GO cue, swapped by the rAF loop
  // Snack Catch: the chibi slides along the ground under this wrapper.
  const chibiSlideRef = useRef(null);
  const rallyTimersRef = useRef([]);
  const clearRallyTimers = () => {
    rallyTimersRef.current.forEach(clearTimeout);
    rallyTimersRef.current = [];
  };

  const flashCare = (msg) => {
    setCareFlash(msg);
    if (careFlashTimer.current) clearTimeout(careFlashTimer.current);
    careFlashTimer.current = setTimeout(() => setCareFlash(null), 6000);
  };

  const doCare = async (action) => {
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/pet/care`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.ok) {
        const prevStats = pet?.stats || {};
        setPet((prev) => prev ? { ...prev, stats: json.stats, mood_label: json.mood_label, total_care: json.total_care || prev.total_care } : prev);
        setCareCooldowns((prev) => ({ ...prev, [action]: Date.now() + 30 * 1000 }));
        flashCare(json.message);
        // Retrigger-safe: drop the class, then re-add on the next frame so
        // rapid successive cares each get a fresh bounce.
        setCareBounce(false);
        if (careBounceTimer.current) clearTimeout(careBounceTimer.current);
        requestAnimationFrame(() => setCareBounce(true));
        careBounceTimer.current = setTimeout(() => setCareBounce(false), 800);
        // Success jingle synced to the vignette kickoff.
        playSfx(action, { quiet: petAsleep });
        // Per-action reaction vignette — keyed by id so a rapid different-
        // action care restarts fresh (same action is cooldown-gated anyway).
        const fxId = Date.now();
        setCareFx({ action, id: fxId });
        if (careFxTimer.current) clearTimeout(careFxTimer.current);
        careFxTimer.current = setTimeout(() => setCareFx(null), 1700);
        if (action === 'pet') {
          const pool = petAsleep ? ['💜', '💤', '✨', '💜'] : ['💜', '💗', '✨', '🥰', '💜'];
          setCareHearts(Array.from({ length: 5 }, (_, i) => ({
            id: `${fxId}-${i}`,
            emoji: pool[Math.floor(Math.random() * pool.length)],
            left: 8 + Math.random() * 76,
            delay: i * 0.12,
          })));
          if (careHeartsTimer.current) clearTimeout(careHeartsTimer.current);
          careHeartsTimer.current = setTimeout(() => setCareHearts([]), 2200);
        }
        // Stat-delta pops on the HUD rows that actually went up.
        const deltas = {};
        ['hunger', 'energy', 'mood'].forEach((k) => {
          const d = Math.round((json.stats?.[k] ?? 0) - (prevStats[k] ?? 0));
          if (d > 0) deltas[k] = { from: prevStats[k] ?? 0, delta: d };
        });
        if (Object.keys(deltas).length > 0) {
          setStatDelta({ id: fxId, deltas });
          if (statDeltaTimer.current) clearTimeout(statDeltaTimer.current);
          statDeltaTimer.current = setTimeout(() => setStatDelta(null), 1500);
        }
      } else {
        playSfx('nope', { quiet: petAsleep });
        flashCare(json?.message || 'Claude didn’t notice… 🤔');
        if (json?.reason === 'rate_limited') {
          setCareCooldowns((prev) => ({ ...prev, [action]: Date.now() + 30 * 1000 }));
        }
      }
    } catch (e) {
      playSfx('nope', { quiet: petAsleep });
      flashCare('Couldn’t reach Claude 📡');
    }
  };

  // ── PLAY arcade handlers (shared shell for every mini-game) ────────────
  // Shuffle bag: deal each game once per cycle, reshuffle when empty.
  const gameBagRef = useRef([]);
  // Rally hearts live in a ref because the rally effect re-runs per volley
  // (streak in deps) — a `let` inside it would refill every hit. The state
  // field `lives` mirrors it for the HUD only.
  const rallyLivesRef = useRef(RALLY_LIVES);
  const drawGame = () => {
    // Test hook: probes force a specific game so runs are deterministic.
    const forced = typeof window !== 'undefined' && window.__claudeTamaForceGame;
    if (forced && GAME_DEFS[forced]) return forced;
    if (gameBagRef.current.length === 0) {
      gameBagRef.current = [...GAME_KEYS].sort(() => Math.random() - 0.5);
    }
    return gameBagRef.current.pop();
  };

  const startRally = () => {
    clearRallyTimers();
    const id = Date.now();
    // Sleepy is decided at serve time and holds for the whole round — a
    // sleep-window flip mid-round shouldn't change the physics under you.
    rallyLivesRef.current = RALLY_LIVES;
    setRally({ id, phase: 'intro', streak: 0, lives: RALLY_LIVES, result: null, sleepy: petAsleep, game: drawGame() });
    rallyTimersRef.current.push(setTimeout(() => {
      setRally((r) => (r && r.id === id ? { ...r, phase: 'play' } : r));
    }, 1300));
  };

  // Every game funnels here: it decides its own score/win and how it ended.
  const finishGame = ({ score, win, result }) => {
    const r = rallyStateRef.current;
    if (!r || r.phase !== 'play') return;
    const def = GAME_DEFS[r.game] || GAME_DEFS.rally;
    try {
      const best = Number(localStorage.getItem(def.bestKey) || 0);
      if (score > best) localStorage.setItem(def.bestKey, String(score));
    } catch (e) { /* private mode etc — best-streak is a nicety */ }
    rallyTickRef.current = { mode: 'idle', t: 0 };
    setRally({ ...r, streak: score, phase: 'over', result: win ? 'win' : result });
    if (win) {
      // The round counts: fire the real care action — jingle, vignette,
      // stat deltas, cooldown, and any server rejection all flow through
      // the normal path.
      doCare('play');
    } else {
      playSfx('nope', { quiet: !!r.sleepy });
    }
    clearRallyTimers();
    rallyTimersRef.current.push(setTimeout(() => setRally(null), win ? 1500 : 1700));
  };
  // The rAF loops call finishGame; route through a ref so effects don't
  // need the (re-created every render) closure in their deps. Same for the
  // per-game tap handler — each game's effect installs its own.
  const finishGameRef = useRef(null);
  finishGameRef.current = finishGame;
  const gameTapRef = useRef(null);
  // Release handler — breathe (hold = inhale) and snack (hold = walk) use
  // it; the tap games leave it null. Move handler = snack steering.
  const gameReleaseRef = useRef(null);
  const gameMoveRef = useRef(null);
  // Score-pop riser over Claude's head — shared by the games.
  const spawnScoreFx = (id, text) => {
    const fx = { id, emoji: text, left: 30 + Math.random() * 40 };
    setPokeFx((p) => [...p.slice(-4), fx]);
    setTimeout(() => setPokeFx((p) => p.filter((f) => f.id !== fx.id)), 1300);
  };
  const spawnScoreFxRef = useRef(null);
  spawnScoreFxRef.current = spawnScoreFx;

  const onRallyTap = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const r = rallyStateRef.current;
    if (!r || r.phase !== 'play') return;
    gameTapRef.current?.(e);
  };
  const onRallyRelease = (e) => {
    e.stopPropagation();
    const r = rallyStateRef.current;
    if (!r || r.phase !== 'play') return;
    gameReleaseRef.current?.(e);
  };
  const onRallyMove = (e) => {
    const r = rallyStateRef.current;
    if (!r || r.phase !== 'play') return;
    gameMoveRef.current?.(e);
  };

  // Rally guardrails: cancel if the carousel rotates the card off-center or
  // the explorer goes live mid-round (the offline strip unmounts under us).
  useEffect(() => {
    if ((!isCenter || isLiveNow) && rallyStateRef.current) {
      clearRallyTimers();
      setRally(null);
    }
  }, [isCenter, isLiveNow]);
  useEffect(() => () => clearRallyTimers(), []);

  // Provisioning rotator — runs only while is_live=true but agent_url=null
  // (the bot is booting). Stops the moment the agent_url lands so the live
  // copy can take over.
  const isProvisioningForRotator = !!explorerStatus?.is_live && !explorerStatus?.agent_url;
  useEffect(() => {
    if (!isProvisioningForRotator) return undefined;
    const id = setInterval(() => {
      setProvisioningIdx((i) => (i + 1) % PROVISIONING_PHRASES.length);
    }, PROVISIONING_ROTATE_MS);
    return () => clearInterval(id);
  }, [isProvisioningForRotator]);

  // Current displayed snippet — uses idx mod length so a smaller pool still
  // rotates cleanly.
  const currentSnippet = snippetPool.length > 0
    ? snippetPool[snippetIdx % snippetPool.length]
    : null;
  // Icon + verb (small, on the left of the snippet). Falls back to the
  // verb-only display when no snippet is ready yet.
  const tickerEmoji = activityVerb ? (ACTIVITY_EMOJI[activityVerb] || '💭') : '💭';

  const isLoading = !explorerStatus;
  const isLive = !!explorerStatus?.is_live;
  const agentUrl = explorerStatus?.agent_url || null;
  // Viewer identity (port spec §4.5): a live marketplace expedition carries
  // the ported agent's name + portrait in /explorer/status. Null on classic
  // house sessions (and every offline state — the pet is always the house
  // Claude), so all offline copy stays untouched.
  const marketplaceAgentName = isLive ? (explorerStatus?.agent_name || null) : null;
  const rawMarketplaceAvatarUrl = isLive ? (explorerStatus?.agent_avatar_url || null) : null;
  const liveTitleName = marketplaceAgentName || 'Claude the Explorer';
  // A portrait that 404s falls back to the crab rig; reset per URL so a new
  // session's portrait gets a fresh chance.
  const [portraitFailedUrl, setPortraitFailedUrl] = useState(null);
  const marketplaceAvatarUrl = (rawMarketplaceAvatarUrl && rawMarketplaceAvatarUrl !== portraitFailedUrl)
    ? rawMarketplaceAvatarUrl
    : null;
  // is_live=true but agent_url=null means the session is provisioning — the
  // bot's ECS service is spinning up, the chromium hasn't booted yet, nothing
  // is actually clickable. Distinct user-facing message so the "Live now"
  // copy only appears when there's actually something to interact with.
  const isProvisioning = isLive && !agentUrl;
  const nextLiveAt = explorerStatus?.next_live_at || null;
  const msUntilLive = nextLiveAt ? Math.max(0, nextLiveAt - now) : null;
  const currentSpaceUrl = explorerStatus?.current_space_url || null;
  // Real title from the bot (via document.title in report_location MCP
  // tool → webhook → Lambda → here). Authoritative when present, since
  // the bot literally read the page title. Falls back to URL derivation
  // below when null (e.g. older bot images, or sites without titles).
  //
  // Reject the bare "Space Explorer" (root <title> from public/index.html
  // — every page inherits it unless a route sets its own). Treating that
  // as "no real name reported" lets URL-derivation fill in.
  //
  // Strip a trailing " | Space Explorer" brand suffix when present —
  // useDocumentTitle on per-page routes uses "<NAME> | Space Explorer"
  // for browser-tab clarity, but we want clean "Now at <NAME>" on the
  // rail card.
  const rawReportedName = explorerStatus?.current_space_name || null;
  const reportedSpaceName = (() => {
    if (!rawReportedName) return null;
    const trimmed = rawReportedName.trim();
    if (trimmed === 'Space Explorer') return null;
    const stripped = trimmed.replace(/\s*\|\s*Space Explorer\s*$/, '').trim();
    return stripped.length > 0 ? stripped : null;
  })();

  // Real rendered strip height — the shell bezel's side padding narrows the
  // strip, and aspect-ratio then shortens it below the passed stripHeight.
  const actualStripHeight = (cardWidth && stripAspect)
    ? Math.round((cardWidth - SHELL_PAD * 2) / stripAspect)
    : (stripHeight ?? 220);

  // Pet HUD visibility — shared by the care-button row and the meta strip
  // (which pads its title down to clear the centered buttons).
  const showPetHud = !isLoading && !isProvisioning && !isLive && isCenter && !!pet;

  // Vitals pill (stat hearts) — ONE renderer for both homes so the delta
  // pop/riser behavior can't drift between layouts. Desktop: 3-row column
  // overlaid bottom-left of the screen strip (as always). Phone: a single
  // lit-hearts line docked bottom-right of the META strip — the white band
  // in dark mode (Nina 08-01; every in-strip spot crowded the ground band
  // against the vote chip / care buttons).
  const renderVitalsPill = (inMetaStrip) => (
    <div
      style={{
        position: 'absolute',
        ...(inMetaStrip ? { right: 14, bottom: 8 } : { left: 10, bottom: 8 }),
        zIndex: 4,
        display: 'flex',
        flexDirection: inMetaStrip ? 'row' : 'column',
        alignItems: inMetaStrip ? 'center' : undefined,
        gap: inMetaStrip ? 8 : 1,
        padding: '4px 7px',
        borderRadius: 8,
        background: 'rgba(0,0,0,0.4)',
      }}
    >
      {[
        { statKey: 'hunger', emoji: '🍖', value: pet?.stats?.hunger },
        { statKey: 'energy', emoji: '⚡', value: pet?.stats?.energy },
        { statKey: 'mood', emoji: '💜', value: pet?.stats?.mood },
      ].map(({ statKey, emoji, value }) => {
        const lit = Math.max(0, Math.min(5, Math.round((value || 0) / 20)));
        // Rows that a care action just bumped get their newly-lit hearts
        // popped in + a "+N" riser (statDelta clears ~1.5s later; keying on
        // the delta id remounts for the one-shot).
        const delta = statDelta?.deltas?.[statKey] || null;
        const prevLit = delta ? Math.max(0, Math.min(5, Math.round((delta.from || 0) / 20))) : lit;
        return (
          <div key={emoji} style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
            <span style={{ fontSize: 10, width: 13, textAlign: 'center' }}>{emoji}</span>
            <span style={{ display: 'inline-flex', gap: 1 }}>
              {/* Meta-strip line drops the dim placeholder hearts — lit
                  count still reads, 15 glyphs don't. justLit hearts are
                  always lit, so the pop-in survives. */}
              {[0, 1, 2, 3, 4].filter((i) => !inMetaStrip || i < lit).map((i) => {
                const justLit = delta && i >= prevLit && i < lit;
                return (
                  <span
                    key={justLit ? `${statDelta.id}-${i}` : i}
                    style={{
                      fontSize: 10,
                      lineHeight: 1.2,
                      display: 'inline-block',
                      color: i < lit ? '#ff6bd6' : 'rgba(255,255,255,0.18)',
                      textShadow: i < lit ? '0 0 3px rgba(255,107,214,0.5)' : 'none',
                      animation: justLit ? 'claudeExplorerHeartPop 0.6s ease-out' : undefined,
                    }}
                  >
                    ♥
                  </span>
                );
              })}
            </span>
            {delta && (
              <span
                key={`delta-${statDelta.id}`}
                aria-hidden
                style={{
                  position: 'absolute',
                  // Horizontal line: the riser pops ABOVE its stat instead
                  // of trailing right into the neighbor. right-anchored,
                  // NOT translateX centering: the pop keyframes animate
                  // transform and would stomp it.
                  ...(inMetaStrip
                    ? { bottom: '100%', right: 0, marginBottom: 2 }
                    : { left: '100%', marginLeft: 5 }),
                  fontFamily: 'monospace',
                  fontSize: 10,
                  fontWeight: 800,
                  color: '#ff6bd6',
                  textShadow: '0 1px 3px rgba(0,0,0,0.7)',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  animation: 'claudeExplorerStatDelta 1.4s ease-out forwards',
                }}
              >
                +{delta.delta}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  // Chibi scales with the strip so he doesn't rattle around in desktop /
  // fullscreen cards: ~58% of strip height, floor 118px (was 70% / 140 —
  // user 07-16: his head crowded the countdown + grade shelf on short
  // cards). He stays bottom-anchored, so all the reclaimed room opens up
  // at the top where the clock lives.
  const chibiSize = Math.max(118, Math.min(190, Math.round(actualStripHeight * 0.58)));
  // Avatar overlay sits with its center on the strip/meta boundary.
  const avatarTop = actualStripHeight - 32; // 64px avatar / 2
  // Care-vignette geometry (the fx keyframes below interpolate these):
  // how far the FEED snack rises to reach his mouth, and how far the PLAY
  // ball travels across the strip.
  const foodRise = Math.round(chibiSize * 0.5);
  const ballTravel = Math.round(((cardWidth || 320) - SHELL_PAD * 2) * 0.6);

  // Rally geometry — shared by the loop effect and the render (target pad).
  // Contact point = Claude's right paw side; wall = just inside the right
  // bezel edge. All in strip-local px (ball is absolutely positioned at the
  // strip's bottom-left and moved via translate).
  const rallyStripW = (cardWidth || 320) - SHELL_PAD * 2;
  const rallyContactX = Math.round(rallyStripW / 2 + chibiSize * 0.26);
  const rallyPhase = rally ? rally.phase : null;
  const rallyStreak = rally ? rally.streak : 0;
  const rallyId = rally ? rally.id : null;
  const rallySleepy = rally ? !!rally.sleepy : false;
  const rallyGame = rally ? (rally.game || 'rally') : 'rally';
  const rallyDef = GAME_DEFS[rallyGame] || GAME_DEFS.rally;
  // Rally restarts its loop per volley (streak in deps); the other games run
  // one continuous loop and only report score, so their key stays 0 and a
  // score update can't reset the physics under the player.
  const rallyLoopKey = rallyGame === 'rally' ? rallyStreak : 0;

  // Game loop — one rAF engine, switched by the drawn game. Rally re-runs
  // per volley (rallyLoopKey); the others run one continuous loop per round.
  // All 60fps writes (transforms/glow) go straight to the DOM via refs;
  // React only hears about score changes and the finish.
  useEffect(() => {
    if (rallyPhase !== 'play') {
      rallyTickRef.current = { mode: 'idle', t: 0 };
      return undefined;
    }
    const H = actualStripHeight;
    const groundY = 30;
    const pace = rallySleepy ? RALLY_SLEEPY_SPEED : 1;
    let raf;
    const done = () => {
      cancelAnimationFrame(raf);
      gameTapRef.current = null;
    };

    // ── RALLY ──
    if (rallyGame === 'rally') {
      const wallX = rallyStripW - 26;
      const contactX = rallyContactX;
      const hopH = Math.max(34, Math.min(64, Math.round(H * 0.28)));
      const approachMs = Math.round(
        Math.max(RALLY_MIN_MS, Math.round(RALLY_START_MS * Math.pow(RALLY_SPEEDUP, rallyLoopKey))) * pace,
      );
      // Learning curve: the first two volleys get a wider window; sleepy
      // stays hard (that's its identity).
      const baseFrac = rallyLoopKey < 2 ? RALLY_EASY_HIT_FRAC : RALLY_HIT_FRAC;
      const hitFrac = rallySleepy ? RALLY_SLEEPY_HIT_FRAC : baseFrac;
      const returnMs = rallyLoopKey === 0 ? 0 : Math.max(240, Math.round(approachMs * 0.5));
      let mode = returnMs > 0 ? 'return' : 'approach';
      let start = performance.now();
      let wasHot = false;
      const step = (ts) => {
        const el = rallyBallRef.current;
        if (!el) { raf = requestAnimationFrame(step); return; }
        if (mode === 'return') {
          // Volley flying back to the wall — non-interactive victory lap.
          const t = Math.min(1, (ts - start) / returnMs);
          const x = contactX + (wallX - contactX) * t;
          const y = groundY + Math.sin(Math.PI * t) * hopH * 0.7;
          el.style.transform = `translate(${x}px, ${-y}px)`;
          rallyTickRef.current = { mode, t };
          if (t >= 1) { mode = 'approach'; start = ts; }
          raf = requestAnimationFrame(step);
          return;
        }
        const t = (ts - start) / approachMs;
        if (t < 0) {
          // Re-serve pause after a lost heart — ball waits at the wall.
          // Tick must go stale-proof: the last written t was ~1 (hot), and
          // a tap reading that would score a phantom hit.
          rallyTickRef.current = { mode, t };
          el.style.transform = `translate(${wallX}px, ${-groundY}px)`;
          el.style.filter = 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))';
          if (rallyZoneRef.current) {
            rallyZoneRef.current.style.opacity = '0.35';
            rallyZoneRef.current.style.boxShadow = 'none';
          }
          raf = requestAnimationFrame(step);
          return;
        }
        if (t >= 1) {
          // Ball got past Claude — costs a heart, not the round.
          rallyLivesRef.current -= 1;
          if (rallyLivesRef.current <= 0) {
            finishGameRef.current({ score: rallyLoopKey, win: rallyLoopKey >= GAME_DEFS.rally.goal, result: 'miss' });
            return;
          }
          playSfx('nope', { quiet: rallySleepy });
          spawnScoreFxRef.current(`rally-life-${Math.round(ts)}`, '💔');
          setRally((r) => (r && r.phase === 'play' ? { ...r, lives: rallyLivesRef.current } : r));
          wasHot = false;
          start = ts + 420; // a beat to reset your eye before the next serve
          raf = requestAnimationFrame(step);
          return;
        }
        const x = wallX + (contactX - wallX) * t;
        // Two-hop approach: a big bounce then a short skip into the contact.
        const y = t < RALLY_HOP_SPLIT
          ? groundY + Math.sin(Math.PI * (t / RALLY_HOP_SPLIT)) * hopH
          : groundY + Math.sin(Math.PI * ((t - RALLY_HOP_SPLIT) / (1 - RALLY_HOP_SPLIT))) * hopH * 0.45;
        const inWindow = t >= 1 - hitFrac;
        if (inWindow && !wasHot) {
          // The "tap NOW" moment — audible tick so timing isn't guesswork.
          wasHot = true;
          playWindowTick({ quiet: rallySleepy });
        } else if (!inWindow) {
          wasHot = false;
        }
        el.style.transform = `translate(${x}px, ${-y}px)`;
        el.style.filter = inWindow
          ? 'drop-shadow(0 0 7px rgba(185,255,158,0.95))'
          : 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))';
        if (rallyZoneRef.current) {
          rallyZoneRef.current.style.opacity = inWindow ? '1' : '0.35';
          rallyZoneRef.current.style.boxShadow = inWindow ? '0 0 12px rgba(185,255,158,0.9)' : 'none';
        }
        rallyTickRef.current = { mode, t };
        raf = requestAnimationFrame(step);
      };
      gameTapRef.current = () => {
        const { mode: m, t } = rallyTickRef.current;
        const r = rallyStateRef.current;
        if (!r) return;
        // Excited taps while the volley flies back (or during the re-serve
        // pause) are free — there's no ball to hit yet.
        if (m === 'return' || t < 0) return;
        if (m === 'approach' && t >= 1 - hitFrac) {
          const newStreak = r.streak + 1;
          try { navigator.vibrate?.(10); } catch (err) { /* unsupported */ }
          playRallyHit(newStreak, { quiet: rallySleepy });
          spawnScoreFxRef.current(`rally-${r.id}-${newStreak}`, `x${newStreak}`);
          setRally({ ...r, streak: newStreak });
        } else {
          // Swung early — costs a heart. The ball keeps coming!
          rallyLivesRef.current -= 1;
          if (rallyLivesRef.current <= 0) {
            finishGameRef.current({ score: r.streak, win: r.streak >= GAME_DEFS.rally.goal, result: 'whiff' });
            return;
          }
          playSfx('nope', { quiet: rallySleepy });
          spawnScoreFxRef.current(`rally-whiff-${Date.now()}`, '💔');
          setRally({ ...r, lives: rallyLivesRef.current });
        }
      };
      raf = requestAnimationFrame(step);
      return done;
    }

    // ── SNACK CATCH ──
    if (rallyGame === 'snack') {
      const chibiSizeNow = Math.max(140, Math.min(210, Math.round(H * 0.7)));
      const catchY = groundY + Math.round(chibiSizeNow * 0.3);
      const topY = H - 18;
      const maxOff = Math.max(40, Math.round(rallyStripW / 2) - 46);
      let idx = 0;
      let catches = 0;
      let chibiX = 0;          // slide offset from center
      let moveTarget = null;   // strip-local pointer x while held
      let keyDir = 0;          // -1/0/1 from arrow keys (desktop steering)
      let facing = 1;
      let start = performance.now();
      let gapUntil = 0;
      let lastTs = null;
      const fallMsFor = (i) => Math.round(Math.max(SNACK_MIN_MS, SNACK_FALL_MS - i * 55) * pace);
      // Deterministic lanes across [15%, 85%] of the strip — random enough
      // to demand movement, stable enough for probes.
      const laneFor = (i) => Math.round((0.15 + (((i * 53) % 71) / 70) * 0.7) * rallyStripW);
      const advance = (ts) => {
        idx += 1;
        if (idx >= SNACK_TOTAL) {
          finishGameRef.current({ score: catches, win: catches >= GAME_DEFS.snack.goal, result: 'splat' });
          return false;
        }
        gapUntil = ts + 320;
        start = ts + 320;
        return true;
      };
      const step = (ts) => {
        const el = rallyBallRef.current;
        const slide = chibiSlideRef.current;
        if (!el || !slide) { raf = requestAnimationFrame(step); return; }
        const dt = lastTs == null ? 0 : Math.min(50, ts - lastTs);
        lastTs = ts;
        // Steer: arrow keys win (desktop), else walk toward the held pointer.
        if (keyDir !== 0) {
          chibiX += keyDir * SNACK_WALK_SPEED * (dt / 1000);
          facing = keyDir;
          chibiX = Math.max(-maxOff, Math.min(maxOff, chibiX));
        } else if (moveTarget != null) {
          const dx = moveTarget - (rallyStripW / 2 + chibiX);
          const stepPx = SNACK_WALK_SPEED * (dt / 1000);
          if (Math.abs(dx) > stepPx) {
            chibiX += Math.sign(dx) * stepPx;
            facing = dx < 0 ? -1 : 1;
          } else {
            chibiX += dx;
          }
          chibiX = Math.max(-maxOff, Math.min(maxOff, chibiX));
        }
        slide.style.transform = `translateX(${Math.round(chibiX)}px) scaleX(${facing})`;
        if (ts < gapUntil) {
          el.style.transform = 'translate(-60px, 0px)';
          rallyTickRef.current = { mode: 'gap', t: 0 };
          raf = requestAnimationFrame(step);
          return;
        }
        const t = (ts - start) / fallMsFor(idx);
        const snackX = laneFor(idx);
        if (t >= 1) {
          // Mouth height — did he get under it?
          const mouthX = rallyStripW / 2 + chibiX;
          if (Math.abs(snackX - mouthX) <= SNACK_CATCH_RADIUS) {
            catches += 1;
            try { navigator.vibrate?.(10); } catch (err) { /* unsupported */ }
            playCatchSfx(catches, { quiet: rallySleepy });
            spawnScoreFxRef.current(`nom-${idx}`, 'nom!');
            setRally((r) => (r && r.phase === 'play' ? { ...r, streak: catches } : r));
          } else {
            spawnScoreFxRef.current(`splat-${idx}`, '💥');
          }
          if (!advance(ts)) return;
          raf = requestAnimationFrame(step);
          return;
        }
        el.textContent = SNACK_EMOJIS[idx % SNACK_EMOJIS.length];
        const y = topY - t * (topY - catchY);
        el.style.transform = `translate(${snackX}px, ${-y}px)`;
        el.style.filter = t >= 0.72
          ? 'drop-shadow(0 0 7px rgba(185,255,158,0.95))'
          : 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))';
        rallyTickRef.current = { mode: 'fall', t };
        raf = requestAnimationFrame(step);
      };
      const stripXFromEvent = (e) => {
        const rect = e.currentTarget ? e.currentTarget.getBoundingClientRect() : null;
        if (!rect || typeof e.clientX !== 'number') return null;
        return e.clientX - rect.left;
      };
      gameTapRef.current = (e) => {
        const x = stripXFromEvent(e);
        if (x != null) moveTarget = x;
      };
      gameMoveRef.current = (e) => {
        if (moveTarget == null) return; // steer only while held
        const x = stripXFromEvent(e);
        if (x != null) moveTarget = x;
      };
      gameReleaseRef.current = () => { moveTarget = null; };
      // Desktop steering: arrow keys (and A/D) while the round runs.
      const dirForKey = (key) => {
        if (key === 'ArrowLeft' || key === 'a' || key === 'A') return -1;
        if (key === 'ArrowRight' || key === 'd' || key === 'D') return 1;
        return 0;
      };
      const onKeyDown = (e) => {
        const d = dirForKey(e.key);
        if (d === 0) return;
        e.preventDefault();
        keyDir = d;
      };
      const onKeyUp = (e) => {
        if (dirForKey(e.key) === keyDir) keyDir = 0;
      };
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup', onKeyUp);
      // Capture the node now — cleanup must reset the SAME element this
      // round animated (ref.current may have moved on by then).
      const slideNode = chibiSlideRef.current;
      raf = requestAnimationFrame(step);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
        gameTapRef.current = null;
        gameMoveRef.current = null;
        gameReleaseRef.current = null;
        if (slideNode) slideNode.style.transform = '';
      };
    }

    // ── FLAPPY CRAB ──
    if (rallyGame === 'flappy') {
      const gapFrac = rallySleepy ? FLAPPY_SLEEPY_GAP_FRAC : FLAPPY_GAP_FRAC;
      const gapH = Math.round(H * gapFrac);
      const speed = FLAPPY_SPEED * (rallySleepy ? 0.85 : 1);
      const flyerX = Math.round(rallyStripW * 0.3);
      const spacing = Math.max(150, Math.round(rallyStripW * 0.6));
      const pillarW = 26;
      const gapBotFor = () => Math.round(H * 0.16 + Math.random() * Math.max(20, H - gapH - H * 0.16 - H * 0.14));
      const gates = [0, 1, 2].map((i) => ({ x: rallyStripW + 40 + i * spacing, gapBot: gapBotFor(), passed: false }));
      let y = H * 0.55;
      let vy = 0;
      let flying = false; // hovers until the first tap
      let gatesPassed = 0;
      let last = performance.now();
      const step = (ts) => {
        const flyer = flappyFlyerRef.current;
        if (!flyer) { raf = requestAnimationFrame(step); return; }
        const dt = Math.min(0.04, (ts - last) / 1000);
        last = ts;
        if (flying) {
          vy -= FLAPPY_GRAVITY * dt;
          y += vy * dt;
        } else {
          y = H * 0.55 + Math.sin(ts / 300) * 6; // pre-game hover bob
        }
        if (y > H - 26) { y = H - 26; vy = Math.min(vy, 0); } // soft ceiling
        flyer.style.transform = `translate(${flyerX - FLAPPY_FLYER_SIZE / 2}px, ${-y}px)`;
        let crashed = flying && y <= groundY + 4;
        // The sprite is bottom-anchored: y is its bottom edge. Collide from
        // its visual center — using y directly stole ~11px of gap below the
        // crab (phantom bonks near the bottom kelp) while giving free
        // overlap above.
        const cy = y + FLAPPY_FLYER_SIZE / 2;
        gates.forEach((g, i) => {
          if (flying) g.x -= speed * dt;
          if (g.x < -pillarW) { g.x += spacing * 3; g.gapBot = gapBotFor(); g.passed = false; }
          const els = flappyGatesRef.current[i];
          if (els && els.top && els.bot) {
            els.bot.style.transform = `translateX(${Math.round(g.x)}px)`;
            els.bot.style.height = `${g.gapBot}px`;
            els.top.style.transform = `translateX(${Math.round(g.x)}px)`;
            els.top.style.bottom = `${g.gapBot + gapH}px`;
            els.top.style.height = `${Math.max(0, H - g.gapBot - gapH)}px`;
          }
          const withinX = Math.abs(g.x + pillarW / 2 - flyerX) < pillarW / 2 + FLAPPY_FLYER_SIZE * FLAPPY_HIT_R;
          if (flying && withinX) {
            const r = FLAPPY_FLYER_SIZE * FLAPPY_HIT_R;
            if (cy - r < g.gapBot || cy + r > g.gapBot + gapH) crashed = true;
          }
          if (flying && !g.passed && g.x + pillarW < flyerX - FLAPPY_FLYER_SIZE / 2) {
            g.passed = true;
            gatesPassed += 1;
            playRallyHit(gatesPassed, { quiet: rallySleepy });
            setRally((r2) => (r2 && r2.phase === 'play' ? { ...r2, streak: gatesPassed } : r2));
            if (gatesPassed >= FLAPPY_CAP) {
              finishGameRef.current({ score: gatesPassed, win: true, result: 'win' });
              crashed = false;
            }
          }
        });
        const st = rallyStateRef.current;
        if (!st || st.phase !== 'play') return; // cap-win above ended it
        if (crashed) {
          finishGameRef.current({ score: gatesPassed, win: gatesPassed >= GAME_DEFS.flappy.goal, result: 'bonk' });
          return;
        }
        raf = requestAnimationFrame(step);
      };
      gameTapRef.current = () => {
        flying = true;
        vy = FLAPPY_IMPULSE;
        playFlapSfx({ quiet: rallySleepy });
        try { navigator.vibrate?.(8); } catch (err) { /* unsupported */ }
      };
      raf = requestAnimationFrame(step);
      return done;
    }

    // ── BREATHE ──
    // Agreement scoring: every frame inside a cycle, "held during inhale /
    // released during exhale" accumulates agreement time. A cycle syncs
    // when agreement ≥ the bar. Ignoring the game entirely agrees ~50% of
    // the time — below every bar, so idling never wins.
    const cycleDurs = BREATHE_CYCLES_MS.map((ms) => Math.round(ms * (rallySleepy ? BREATHE_SLEEPY_STRETCH : 1)));
    const syncBar = rallySleepy ? BREATHE_SLEEPY_SYNC_FRAC : BREATHE_SYNC_FRAC;
    let cycle = 0;
    let cycleStart = 0; // ms into the round where the current cycle began
    let synced = 0;
    let held = false;
    let agreeMs = 0;
    let discVal = 0;      // your-breath disc fill, eased toward held
    let announced = -1;   // last phase we played a guide tone for (2*cycle+half)
    let lastTs = null;
    const start = performance.now() + 400; // settle-in
    const step = (ts) => {
      const ring = beatRingRef.current;
      if (!ring) { raf = requestAnimationFrame(step); return; }
      const since = ts - start;
      if (since < 0) { lastTs = ts; raf = requestAnimationFrame(step); return; }
      const cycleMs = cycleDurs[Math.min(cycle, cycleDurs.length - 1)];
      const rolled = since - cycleStart >= cycleMs;
      const frac = Math.min(0.999, (since - cycleStart) / cycleMs);
      const inhale = frac < 0.5;
      const dt = lastTs == null ? 0 : ts - lastTs;
      lastTs = ts;
      if (rolled) {
        // Cycle rolled over — judge the finished breath.
        if (agreeMs / cycleMs >= syncBar) {
          synced += 1;
          playBreathChime(synced, { quiet: rallySleepy });
          spawnScoreFxRef.current(`bsync-${cycle}`, '🫧');
          setRally((r) => (r && r.phase === 'play' ? { ...r, streak: synced } : r));
        } else {
          spawnScoreFxRef.current(`bmiss-${cycle}`, '·');
        }
        cycleStart += cycleMs;
        cycle += 1;
        agreeMs = 0;
        if (cycle >= BREATHE_TOTAL) {
          finishGameRef.current({ score: synced, win: synced >= GAME_DEFS.breathe.goal, result: 'unsync' });
          return;
        }
      }
      if (held === inhale) agreeMs += dt;
      // Guide tone + text cue at each phase turn (rising in, falling out).
      const phaseId = cycle * 2 + (inhale ? 0 : 1);
      if (phaseId !== announced) {
        announced = phaseId;
        playBreathGuide(inhale, cycleMs / 2, { quiet: rallySleepy });
        const prompt = breathePromptRef.current;
        if (prompt) {
          prompt.textContent = inhale ? 'HOLD' : 'LET GO';
          prompt.style.color = inhale ? '#9ecfff' : '#bbb6c8';
        }
      }
      // Guide ring: swells through the inhale, sinks through the exhale.
      // Green while your hold state agrees with his phase — the "am I doing
      // it right?" answer, live.
      const breath = inhale ? frac * 2 : 1 - (frac - 0.5) * 2;
      const eased = Math.sin((breath * Math.PI) / 2);
      ring.style.transform = `translate(-50%, 50%) scale(${(1 + 0.6 * eased).toFixed(3)})`;
      ring.style.opacity = String(0.45 + 0.4 * eased);
      const agree = held === inhale;
      ring.style.borderColor = agree ? '#b9ff9e' : '#9ecfff';
      ring.style.boxShadow = agree ? '0 0 10px rgba(185,255,158,0.55)' : '0 0 10px rgba(158,207,255,0.45)';
      // Your disc chases your hold state.
      discVal += ((held ? 1 : 0) - discVal) * Math.min(1, dt / 260);
      const disc = breatheDiscRef.current;
      if (disc) disc.style.transform = `translate(-50%, 50%) scale(${(0.25 + 1.35 * discVal).toFixed(3)})`;
      rallyTickRef.current = { mode: 'breathe', t: frac };
      raf = requestAnimationFrame(step);
    };
    gameTapRef.current = () => { held = true; };
    gameReleaseRef.current = () => { held = false; };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      gameTapRef.current = null;
      gameReleaseRef.current = null;
    };
  }, [rallyId, rallyPhase, rallyGame, rallyLoopKey, rallySleepy, rallyStripW, rallyContactX, actualStripHeight]);

  // Best-ever score for the in-game counter subtext (per game, localStorage).
  const rallyBest = (() => {
    if (!rally) return 0;
    try { return Number(localStorage.getItem(rallyDef.bestKey) || 0); } catch (e) { return 0; }
  })();

  // Derive a friendly space name from current_space_url. Patterns we
  // handle:
  //   /space/breakdown-cookie-writer-<hash>  → "Breakdown Cookie Writer"
  //   /game-step?gameId=…                    → "playing a game"
  //   /explore                               → "browsing /explore"
  //   anything else                          → null (subtitle falls back)
  // Used only when the bot didn't report a real title (reportedSpaceName
  // is null). Once every space reports a title via report_location, this
  // becomes a vestigial fallback.
  const derivedSpaceName = (() => {
    if (!currentSpaceUrl) return null;
    let u;
    try { u = new URL(currentSpaceUrl); } catch (_) { return null; }
    const segs = u.pathname.split('/').filter(Boolean);
    if (segs[0] === 'space' && segs[1]) {
      // Slug shape: "kebab-case-name-<8charhash>". Strip a trailing
      // <=8-char alnum hash if present, then titlecase the rest.
      let slug = segs[1];
      const hashTail = slug.match(/-([a-z0-9]{6,12})$/i);
      if (hashTail) slug = slug.slice(0, -hashTail[0].length);
      const words = slug.split('-').filter(Boolean);
      if (words.length === 0) return null;
      return words.map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
    }
    if (segs[0] === 'game-step') return 'playing a game';
    if (segs[0] === 'explore') return 'browsing /explore';
    return null;
  })();

  const friendlySpaceName = reportedSpaceName || derivedSpaceName;

  // Current offline activity — drives subtitle + chibi expression + prop.
  // Asleep (or pet endpoint unreachable) pulls from the dream pool; a genuinely
  // hungry pet holds the droopy hungry pool (sticky — see HUNGRY_ACTIVITIES);
  // otherwise the doing-things pool.
  const petAsleep = pet ? !!pet.asleep : true;
  const currentActivity = petAsleep
    ? DREAM_ACTIVITIES[nappingIdx % DREAM_ACTIVITIES.length]
    : ((pet?.stats?.hunger ?? 100) < 30)
      ? HUNGRY_ACTIVITIES[nappingIdx % HUNGRY_ACTIVITIES.length]
      : AWAKE_ACTIVITIES[nappingIdx % AWAKE_ACTIVITIES.length];

  const pokeDown = (e) => {
    // Don't bubble into the carousel tile's click handling.
    e.stopPropagation();
    // Lean away from the poked cheek: pointer left of center pushes him
    // right (positive rotate) and vice versa. Full strength asleep too —
    // the gentle sleepy squish read as "no boing" to users.
    if (typeof e.clientX === 'number' && e.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      const rel = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      setPokeLean(-Math.max(-1, Math.min(1, rel)) * 7);
    }
    setPokePressed(true);
    try { navigator.vibrate?.(12); } catch (err) { /* unsupported */ }
    playSfx('pokeDown', { quiet: petAsleep });
    const pool = petAsleep
      ? ['💤', '😴', '…zzz']
      : ['boop!', '💜', '✨', 'hehe~', '🥰', '!!'];
    const fx = {
      id: `${Date.now()}-${Math.random()}`,
      emoji: pool[Math.floor(Math.random() * pool.length)],
      left: 18 + Math.random() * 64,
    };
    setPokeFx((p) => [...p.slice(-4), fx]);
    setTimeout(() => setPokeFx((p) => p.filter((f) => f.id !== fx.id)), 1300);
  };
  const pokeUp = () => {
    // Release → spring back (keyed remount restarts the boing). The sound
    // only fires off a real press (pointercancel without one stays silent).
    if (pokePressed) playSfx('boing', { quiet: petAsleep });
    setPokePressed(false);
    setPokeReleaseSeq((s) => s + 1);
  };
  // Safety: if the carousel rotates this card off-center mid-hold, the
  // pointer handlers detach and the release would never fire — unstick.
  useEffect(() => {
    if (!isCenter && pokePressed) setPokePressed(false);
  }, [isCenter, pokePressed]);

  // Offline subtitle = what the pet is doing right now (or a transient
  // care-result flash). Live subtitle = "Now at {NAME}" when we can derive
  // a name; falls back to "exploring" otherwise. The big in-strip countdown
  // clock already carries the precise "when next" intel for offline state.
  const subtitle = isLoading
    ? (statusUnreachable ? 'Snoozing between expeditions. Back soon!' : 'Loading…')
    : isProvisioning
      ? PROVISIONING_PHRASES[provisioningIdx]
      : isLive
        ? (friendlySpaceName ? `Now at ${friendlySpaceName}` : 'Live now · exploring')
        : (careFlash || currentActivity.text);

  // Provisioning subtitle uses marketplace-style heavy/upper styling so the
  // wait state reads as deliberate-and-fun ("we're getting ready!") rather
  // than the default subdued grey of regular subtitles.
  const subtitleStyle = isProvisioning
    ? { fontSize: '0.85rem', fontWeight: 800, color: '#e0d5ff', letterSpacing: '0.04em' }
    : { fontSize: '0.82rem', color: '#bbb6c8' };

  return (
    <div
      // Outer wrapper has overflow:visible so the scuttling crab can sit ON
      // the top border without getting clipped. The inner card keeps its
      // overflow:hidden for the rounded corners + iframe clipping.
      style={{
        position: 'relative',
        ...(cardWidth ? { width: `${cardWidth}px` } : null),
      }}
    >
      {/* Crab on the top edge of the card — half above the border, half on
          it. Two jobs depending on state:
            • OFFLINE: he IS the countdown progress bar. His position along
              the rail = how far through the 24h between-sessions wait we
              are (sessions are daily — computeNextLiveAt in
              explorerService.js), crawling left→right toward a flag at the
              live end, with a pixel-dash trail filling in behind him.
              Awake he shuffles in place at his mark; asleep he sways
              (grayscale + Zzz) but still holds the mark.
            • LIVE: progress is meaningless, so he free-scuttles like
              before — excited (<8s since action, fast + warm glow), bored
              (2+ min, slow + desaturated), content otherwise.
          Lives in the outer wrapper so it overhangs the rounded corner clip. */}
      {(() => {
        const secsSince = explorerStatus?.seconds_since_last_action ?? null;
        const mood = !isLive
          ? (petAsleep ? 'sleeping' : 'content')
          : (secsSince != null && secsSince < 8)
            ? 'excited'
            : (secsSince != null && secsSince > 120)
              ? 'bored'
              : 'content';
        // Countdown-progress mapping (offline only). Same walkable span the
        // live walk animation uses, so both modes share the rail's ends.
        const SESSION_INTERVAL_MS = 24 * 60 * 60 * 1000;
        const walkable = Math.max(40, (cardWidth || 320) - 60);
        const liveProgress = (!isLive && msUntilLive != null)
          ? Math.max(0, Math.min(1, 1 - msUntilLive / SESSION_INTERVAL_MS))
          : null;
        const crabX = liveProgress != null ? Math.round(liveProgress * walkable) : null;
        const walkDuration = mood === 'excited' ? '4.5s' : mood === 'bored' ? '14s' : '9s';
        const swayDuration = mood === 'excited' ? '0.7s' : mood === 'bored' ? '3.2s' : mood === 'sleeping' ? '4s' : '1.6s';
        const filterCss = mood === 'excited'
          ? 'drop-shadow(0 2px 6px rgba(255,180,80,0.85))'
          : mood === 'sleeping'
            ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.7)) grayscale(0.6) brightness(0.75)'
            : mood === 'bored'
              ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.7)) grayscale(0.3) brightness(0.85)'
              : 'drop-shadow(0 2px 4px rgba(0,0,0,0.7))';
        return (
          <>
            {/* Progress trail — little sandy footprints left behind the crab
                as the wait burns down (Nina 08-02: the tech-y dashes read as
                a loading bar; prints-in-sand keeps the countdown legible but
                in-world). Two staggered dot rows = left/right claw steps. */}
            {crabX != null && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: -4,
                  left: 8,
                  width: crabX + 14,
                  height: 4,
                  zIndex: 9,
                  pointerEvents: 'none',
                  backgroundImage: `radial-gradient(circle at 2px 1px, rgba(238, 214, 175, 0.95) 1.1px, transparent 1.6px),
                    radial-gradient(circle at 7px 3px, rgba(222, 192, 145, 0.8) 1px, transparent 1.5px)`,
                  backgroundSize: '10px 4px',
                  backgroundRepeat: 'repeat-x',
                  opacity: 0.9,
                  transition: 'width 1s linear',
                }}
              />
            )}
            {/* Finish flag — the "goes live here" end of the rail. GREEN
                (go!), via hue-rotate since no green flag emoji exists.
                Once the vote closes, the flag becomes the WINNER's emoji —
                the countdown walk finally has a destination in-fiction
                (Phase 5): he's scuttling toward where the audience sends
                him. */}
            {crabX != null && (
              <span
                aria-hidden
                title={ballotWinner ? `Next stop: ${ballotWinner.label}` : ballotTie ? 'Vote tied! Chibi decides live' : undefined}
                style={{
                  position: 'absolute',
                  top: -15,
                  left: 8 + walkable + 26,
                  zIndex: 9,
                  fontSize: 13,
                  lineHeight: 1,
                  opacity: 0.85,
                  pointerEvents: 'none',
                  filter: ballotWinner
                    ? 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))'
                    : 'hue-rotate(125deg) saturate(1.4) drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                }}
              >
                {ballotWinner ? ballotWinner.emoji : ballotTie ? '⚖️' : '🚩'}
              </span>
            )}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: -16,
                left: 8,
                zIndex: 10,
                fontSize: 28,
                lineHeight: 1,
                pointerEvents: 'none',
                // Offline: parked at the countdown mark (translate below).
                // Live: free-scuttle loop. Sleeping never walks regardless.
                animation: (crabX != null || mood === 'sleeping') ? 'none' : `claudeExplorerCrabWalk ${walkDuration} ease-in-out infinite`,
                transform: crabX != null ? `translateX(${crabX}px)` : undefined,
                filter: filterCss,
                willChange: 'transform',
                // Progress moves ~0.003px/s — the 1s transform transition is
                // for the initial status-load slide to the mark, not ticks.
                transition: crabX != null ? 'filter 0.6s ease, transform 1s linear' : 'filter 0.6s ease',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  // At his mark, an awake crab shuffles in place so the
                  // progress marker still reads as a living thing.
                  animation: crabX != null && mood !== 'sleeping'
                    ? `claudeExplorerCrabShuffle 2.8s ease-in-out infinite`
                    : 'none',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    animation: `claudeExplorerCrabSway ${swayDuration} ease-in-out infinite`,
                    transformOrigin: 'center',
                  }}
                >
                  🦀
                </span>
              </span>
              {mood === 'sleeping' && (
                <span
                  style={{
                    position: 'absolute',
                    top: -8,
                    left: 22,
                    fontSize: 14,
                    opacity: 0.75,
                    animation: 'claudeExplorerZzz 3s ease-in-out infinite',
                  }}
                >
                  💤
                </span>
              )}
            </div>
            <style>{`
              @keyframes claudeExplorerCrabWalk {
                0%   { transform: translateX(0)    translateY(0)    scaleX(1); }
                42%  { transform: translateX(${Math.max(40, (cardWidth || 320) - 60)}px) translateY(0)    scaleX(1); }
                46%  { transform: translateX(${Math.max(40, (cardWidth || 320) - 60)}px) translateY(-8px) scaleX(1); }
                50%  { transform: translateX(${Math.max(40, (cardWidth || 320) - 60)}px) translateY(-10px) scaleX(-1); }
                54%  { transform: translateX(${Math.max(40, (cardWidth || 320) - 60)}px) translateY(0)    scaleX(-1); }
                92%  { transform: translateX(0)    translateY(0)    scaleX(-1); }
                96%  { transform: translateX(0)    translateY(-8px) scaleX(-1); }
                100% { transform: translateX(0)    translateY(-10px) scaleX(1); }
              }
              @keyframes claudeExplorerCrabSway {
                0%, 100% { transform: translateY(0)    rotate(-3deg); }
                50%      { transform: translateY(-2px) rotate(3deg); }
              }
              @keyframes claudeExplorerCrabShuffle {
                0%, 100% { transform: translateX(0); }
                30%      { transform: translateX(-4px); }
                55%      { transform: translateX(3px); }
                80%      { transform: translateX(-2px); }
              }
              @keyframes claudeExplorerZzz {
                0%   { transform: translateY(0)   scale(0.9); opacity: 0.4; }
                50%  { transform: translateY(-6px) scale(1.1); opacity: 0.9; }
                100% { transform: translateY(-12px) scale(0.8); opacity: 0; }
              }
              @keyframes claudeExplorerCareBounce {
                0%   { transform: translateY(0)     scale(1); }
                30%  { transform: translateY(-12px) scale(1.015); }
                55%  { transform: translateY(0)     scale(0.99); }
                75%  { transform: translateY(-5px)  scale(1.005); }
                100% { transform: translateY(0)     scale(1); }
              }
              /* Whole-shell LIVE ring (user 07-16) — the card itself pulses
                 red while the bot is live. Keyframes carry the shell's
                 inset bezel shadows because an animated box-shadow replaces
                 the inline one wholesale. */
              @keyframes claudeExplorerLiveRing {
                0%, 100% { box-shadow: inset 0 2px 5px rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.28), 0 4px 10px rgba(0,0,0,0.3), 0 0 0 3px rgba(255,56,56,0.55), 0 0 22px 4px rgba(235,4,0,0.35); }
                50%      { box-shadow: inset 0 2px 5px rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.28), 0 4px 10px rgba(0,0,0,0.3), 0 0 0 5px rgba(255,56,56,0.85), 0 0 40px 8px rgba(235,4,0,0.6); }
              }
              /* Phone: ring + a glow that decays inside the rail stage's
                 ~14px clip headroom — the 22-40px glow clips into a
                 hard-edged rectangle (same artifact as the drop shadow) */
              @keyframes claudeExplorerLiveRingPhone {
                0%, 100% { box-shadow: inset 0 2px 5px rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.28), 0 0 0 3px rgba(255,56,56,0.55), 0 0 8px 2px rgba(235,4,0,0.4); }
                50%      { box-shadow: inset 0 2px 5px rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.28), 0 0 0 5px rgba(255,56,56,0.85), 0 0 10px 2px rgba(235,4,0,0.6); }
              }
              /* 8-bit pixel-chunk care buttons — square with stepped pixel
                 corners (clip-path), hard 2-tone shading bands + a dither
                 row, matching the pixel ground + scanlines. Pressing drops
                 the face one "pixel row" (4px, stepped easing) onto the
                 dark base slab. Class-based because inline styles can't
                 express :active; the face is a child span because clip-path
                 would clip an offset box-shadow. */
              .claude-tama-care-btn {
                position: relative;
                width: 54px;
                height: 50px;
                padding: 0;
                border: 0;
                background: transparent;
                cursor: pointer;
              }
              .claude-tama-care-btn::before {
                /* base slab the face sits on — the visible 4px of travel */
                content: '';
                position: absolute;
                left: 4px;
                right: 4px;
                bottom: 0;
                height: 10px;
                background: #161028;
                clip-path: polygon(0 4px, 4px 4px, 4px 0, calc(100% - 4px) 0, calc(100% - 4px) 4px, 100% 4px, 100% calc(100% - 4px), calc(100% - 4px) calc(100% - 4px), calc(100% - 4px) 100%, 4px 100%, 4px calc(100% - 4px), 0 calc(100% - 4px));
              }
              .claude-tama-care-face {
                position: absolute;
                inset: 0 0 4px 0;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 1px;
                background: #241a3f; /* outer chunk = hard dark border */
                clip-path: polygon(0 4px, 4px 4px, 4px 0, calc(100% - 4px) 0, calc(100% - 4px) 4px, 100% 4px, 100% calc(100% - 4px), calc(100% - 4px) calc(100% - 4px), calc(100% - 4px) 100%, 4px 100%, 4px calc(100% - 4px), 0 calc(100% - 4px));
                transition: transform 40ms steps(1, end);
              }
              .claude-tama-care-face::before {
                /* inner fill: hard highlight band / body / shade band with
                   a 4px dither strip at the shade boundary */
                content: '';
                position: absolute;
                inset: 3px;
                clip-path: polygon(0 4px, 4px 4px, 4px 0, calc(100% - 4px) 0, calc(100% - 4px) 4px, 100% 4px, 100% calc(100% - 4px), calc(100% - 4px) calc(100% - 4px), calc(100% - 4px) 100%, 4px 100%, 4px calc(100% - 4px), 0 calc(100% - 4px));
                background:
                  repeating-linear-gradient(90deg, #b48ae8 0 4px, transparent 4px 8px) left calc(100% - 8px) / 100% 4px no-repeat,
                  linear-gradient(180deg, #f6ecff 0 20%, #ddc6fb 20% 74%, #b48ae8 74% 100%);
              }
              .claude-tama-care-face > * {
                position: relative;
                z-index: 1;
              }
              .claude-tama-care-btn:active:not(:disabled) .claude-tama-care-face {
                transform: translateY(4px);
              }
              .claude-tama-care-btn:disabled {
                cursor: not-allowed;
              }
              .claude-tama-care-btn:disabled .claude-tama-care-face::before {
                background: linear-gradient(180deg, #524a63 0 20%, #443c55 20% 100%);
              }
              .claude-tama-care-btn:disabled .claude-tama-care-face {
                opacity: 0.85;
              }
            `}</style>
          </>
        );
      })()}
      {/* Tamagotchi shell bezel — the plastic purple frame from the pet's
          original egg widget, flattened into a border around the tile.
          Always on (it's this card's identity); the inner screen area is
          untouched so the live video stays video-first. The one-shot care
          bounce animates the whole shell. */}
      <div
        // Live: the WHOLE card is the join affordance — side tiles too
        // (Nina 07-17: center-only cost a rotate click first). The show
        // opens in its own tab so the rail keeps the user's place; the
        // theater's tap-to-play pill covers the lost autoplay gesture.
        // Never for the bot's own browser (?bot=1): window.open strands
        // its CDP on the old tab AND its own live tile is the recursive-
        // livestream trap.
        onClick={isLive && new URLSearchParams(window.location.search).get('bot') !== '1' ? (e) => { e.stopPropagation(); window.open('/explorer/theater', '_blank', 'noopener'); } : undefined}
        style={{
          position: 'relative',
          cursor: isLive ? 'pointer' : undefined,
          padding: `${SHELL_PAD}px ${SHELL_PAD}px ${SHELL_PAD + 2}px`,
          borderRadius: 26,
          background: `radial-gradient(circle at 30% 18%, #e6d5ff 0%, ${EXPLORER_ACCENT} 36%, #7b4fd6 80%, #5c16c5 100%)`,
          // Phone drops the outer drop-shadow layers (insets stay — they
          // render inside the shell): the rail stage clips ~14px past the
          // card, so the 60-80px haze gets cut into a hard gray rectangle
          // on the light page (same fix as LiveRailCard, 07-31).
          boxShadow: isCenter && !isPhone
            ? 'inset 0 2px 5px rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.28), 0 4px 10px rgba(0,0,0,0.3), 0 32px 60px rgba(0,0,0,0.45), 0 24px 80px rgba(123,31,162,0.32)'
            : 'inset 0 2px 5px rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.28)',
          animation: [
            careBounce ? 'claudeExplorerCareBounce 0.7s cubic-bezier(0.28, 0.84, 0.42, 1)' : null,
            isLive ? `${isPhone ? 'claudeExplorerLiveRingPhone' : 'claudeExplorerLiveRing'} 2.2s ease-in-out infinite` : null,
          ].filter(Boolean).join(', ') || undefined,
        }}
      >
      <div
        className={`live-rail-card claude-explorer-card${isCenter ? ' is-center' : ' is-side'}`}
        style={{
          position: 'relative',
          borderRadius: 18,
          overflow: 'hidden',
          background: '#15121e',
          width: '100%',
        }}
      >
      {/* Strip — iframe when live, gradient + countdown otherwise */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          ...(stripAspect ? { aspectRatio: `${stripAspect}` } : null),
          background: `radial-gradient(circle at 30% 30%, ${EXPLORER_ACCENT}55, #0e0e10 75%)`,
          overflow: 'hidden',
        }}
      >
        {isLive && agentUrl && isLowResourceDevice ? (
          // 📱 07-19 crash fix: phones don't mount the embedded live viewer —
          // it's a whole HLS-playing page inside this tab's memory budget.
          // Static poster instead; the card tap already opens the full view.
          // Poster face is the REAL chibi (same ChibiAvatar as desktop), not
          // a bare 🦀 — the emoji read as a different character entirely
          // (Nina's mini, 07-31). Still zero video cost.
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              pointerEvents: 'none',
            }}
          >
            <ChibiAvatar
              isLive={isLive}
              secondsSinceLastAction={activitySecondsSince}
              activity={activityVerb}
              size={88}
              bubble={false}
            />
            <div
              style={{
                background: 'rgba(211,47,47,0.92)',
                color: '#fff',
                padding: '4px 12px',
                borderRadius: 999,
                fontWeight: 900,
                fontSize: 12,
                letterSpacing: '1px',
              }}
            >
              🔴 LIVE
            </div>
            <div style={{ color: '#fff', fontSize: 12, fontWeight: 700, opacity: 0.85 }}>
              Tap to watch
            </div>
          </div>
        ) : isLive && agentUrl ? (
          <iframe
            // The agent's root URL has restrictive CSP (frame-ancestors 'none') from
            // the OpenClaw gateway. The /viewer subpath is the dedicated livestream
            // page served by Caddy directly with no CSP — that's what we embed.
            // ?embed=1 suppresses the chat panel + caption param activation inside
            // viewer.html so the preview tile stays clean (no chat UI, no polling).
            src={`${agentUrl.replace(/\/$/, '')}/viewer?embed=1`}
            title={`${liveTitleName} · live`}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              pointerEvents: 'none', // viewers click the card to open full view; iframe is a preview only
            }}
            allow="autoplay"
          />
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              // Bottom-anchored (not centered): the countdown owns the top
              // and the chibi should sit low in the scene at ANY card size —
              // a fixed top offset floats him too high on tall/fullscreen
              // strips. 22px bottom padding (36 → 28 → 22 across two rounds
              // of "top feels crowded" feedback) sinks his feet into the
              // ground band while still clearing the care buttons
              // straddling the boundary.
              justifyContent: 'flex-end',
              gap: 12,
              padding: '16px 16px 22px',
              textAlign: 'center',
            }}
          >
            {/* Souvenir shelf (Phase 4; redesigned 07-16) — the LAST 3
                claw-stamped report cards, tucked under the DAY chip so the
                top-center stays the clock's. Each stamp with a session id is
                a real button: tap → the theater REPLAY of that expedition
                (07-17 queue: stamps → reel links; the replay panel links on
                to the full report, so nothing is lost). Older stamps predate
                session tracking and render unlinked. */}
            {/* Hidden while a minigame runs — the shelf collided with the
                game HUD's title/instructions on narrow phones (Nina, 07-17).
                Phone shows ONE stamp (the latest) — the 3-stamp shelf crowded
                the small tile, but a single report card keeps the grade
                brag + its tap-to-replay (Nina 07-31). */}
            {/* 📱 Phone: no on-card shelf — the latest report card moved
                into the ballot sheet (Nina 08-01), so the card face keeps
                only the pet. Desktop keeps the 3-stamp shelf. */}
            {!isPhone && !isProvisioning && !rally && Array.isArray(pet?.souvenirs) && pet.souvenirs.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 36,
                  left: 10,
                  display: 'flex',
                  gap: 4,
                  zIndex: 4,
                  pointerEvents: 'none',
                }}
              >
                {pet.souvenirs.slice(isPhone ? -1 : -3).map((s, i) => (
                  <button
                    key={`${s.ts || i}`}
                    type="button"
                    title={`${s.grade} · ${s.verdict || ''}${s.dest ? ` (${s.dest})` : ''}${s.session ? ' · tap to replay it' : ''}`}
                    aria-label={s.session ? `Expedition replay: grade ${s.grade}` : `Grade ${s.grade}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (s.session) navigate(`/explorer/theater?replay=${encodeURIComponent(s.session)}`);
                    }}
                    style={{
                      width: 22,
                      height: 26,
                      padding: 0,
                      borderRadius: 4,
                      background: 'linear-gradient(180deg, #fffdf5, #f0e6c8)',
                      border: '1.5px solid rgba(91, 58, 142, 0.7)',
                      color: '#5b3a8e',
                      fontSize: 11,
                      fontWeight: 900,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: `rotate(${(i % 3) - 1}deg)`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                      pointerEvents: 'auto',
                      cursor: s.session ? 'pointer' : 'default',
                    }}
                  >
                    {s.grade}
                  </button>
                ))}
              </div>
            )}
            {/* 🗳️ Destination vote (Phase 5; tamagotchi-native 07-16) — the
                ballot lives as a bezel ALERT CHIP docked top-right, right
                under the AI Explorer persona pill (user 07-16), like the
                classic tamagotchi "needs you" icon: it nudges with a ping
                dot until you vote, shows the live leader once you have, and
                becomes the winner + departure clock when voting closes.
                Tapping slides the ballot up from the bottom of the screen
                as a pixel-LCD panel. Replaces the old floating top pill
                (user 07-16: crowded). */}
            {/* Hidden while a minigame runs — same narrow-phone collision as
                the shelf (the LCD owns the strip during play) */}
            {!isProvisioning && !rally && ballot && Array.isArray(ballot.candidates) && ballot.candidates.length > 0 && (
              <>
                <style>{`
                  @keyframes claudeExplorerBallotSheetUp {
                    from { transform: translateY(105%); }
                    to   { transform: translateY(0); }
                  }
                  @keyframes claudeExplorerVoteNudge {
                    0%, 78%, 100% { transform: translateY(0) rotate(0deg); }
                    84% { transform: translateY(-3px) rotate(-4deg); }
                    90% { transform: translateY(0) rotate(3deg); }
                    96% { transform: translateY(-1px) rotate(0deg); }
                  }
                  @keyframes claudeExplorerVotePing {
                    0%   { box-shadow: 0 0 0 0 rgba(255, 92, 92, 0.7); }
                    70%  { box-shadow: 0 0 0 6px rgba(255, 92, 92, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(255, 92, 92, 0); }
                  }
                `}</style>
                {/* Alert chip — hidden while the sheet is up (tapping its
                    spot then reads as click-outside and closes cleanly).
                    Desktop only: phone's chip renders centered UNDER the
                    card (see renderBallotChipContent + the dock below the
                    shell — Nina 08-01). */}
                {!isPhone && !ballotPeek && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); peekBallot(); }}
                    title={ballotChipTitle}
                    aria-label={ballotChipAria}
                    style={{
                      ...ballotChipBaseStyle,
                      position: 'absolute',
                      right: 10,
                      top: 38, /* just under the persona pill (top:10 + ~24px) */
                      zIndex: 6,
                      animation: (!myVote && !ballotWinner && !ballotTie) ? 'claudeExplorerVoteNudge 2.6s ease-in-out infinite' : undefined,
                    }}
                  >
                    {renderBallotChipContent(true)}
                  </button>
                )}
                {/* Slide-up LCD ballot sheet — anchored to the bottom of the
                    screen like a tamagotchi menu. 34px bottom padding keeps
                    the candidate grid clear of the care buttons straddling
                    the screen boundary. Click-outside (ballotBoxRef effect)
                    and the peek timer both dismiss it. */}
                {ballotPeek && (
                  <div
                    ref={ballotBoxRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 5,
                      padding: '10px 12px 34px',
                      borderTop: `2px solid ${EXPLORER_ACCENT}55`,
                      borderRadius: '14px 14px 0 0',
                      background: 'rgba(14, 9, 28, 0.94)',
                      boxShadow: '0 -6px 24px rgba(0,0,0,0.5)',
                      animation: 'claudeExplorerBallotSheetUp 0.26s cubic-bezier(0.2, 0.9, 0.3, 1)',
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 6,
                        marginBottom: 6,
                        fontFamily: 'monospace',
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        color: voteFlash ? '#ffe08a' : 'rgba(237,231,255,0.8)',
                        transition: 'color 0.3s ease',
                      }}
                    >
                      <span>{voteFlash || (ballotWinner ? '🗳️ NEXT EXPEDITION' : ballotTie ? '⚖️ DEAD HEAT!' : myVote ? '🗳️ THE RACE, LIVE' : '🗳️ WHERE NEXT? YOU DECIDE')}</span>
                      <button
                        type="button"
                        aria-label="Close voting"
                        onClick={(e) => { e.stopPropagation(); closeBallotPeek(); }}
                        style={{
                          width: 18,
                          height: 18,
                          padding: 0,
                          borderRadius: '50%',
                          border: '1px solid rgba(237,231,255,0.35)',
                          background: 'rgba(20, 14, 34, 0.7)',
                          color: 'rgba(237,231,255,0.8)',
                          fontSize: 9,
                          lineHeight: 1,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    {/* 📱 Last report card lives IN the sheet on phone (Nina
                        08-01) — the sheet is the chibi's dashboard: last
                        expedition's grade + where he goes next. Same
                        tap-to-replay as the desktop shelf stamps. */}
                    {isPhone && Array.isArray(pet?.souvenirs) && pet.souvenirs.length > 0 && (() => {
                      const s = pet.souvenirs[pet.souvenirs.length - 1];
                      return (
                        <button
                          type="button"
                          title={`${s.grade} · ${s.verdict || ''}${s.session ? ' · tap to replay it' : ''}`}
                          aria-label={s.session ? `Expedition replay: grade ${s.grade}` : `Grade ${s.grade}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (s.session) navigate(`/explorer/theater?replay=${encodeURIComponent(s.session)}`);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: 6,
                            padding: '4px 8px',
                            borderRadius: 6,
                            border: '1px solid rgba(237,231,255,0.25)',
                            background: 'rgba(30, 18, 52, 0.6)',
                            color: 'rgba(237,231,255,0.85)',
                            fontFamily: 'monospace',
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: '0.06em',
                            cursor: s.session ? 'pointer' : 'default',
                          }}
                        >
                          <span>LAST EXPEDITION</span>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 20, height: 24, borderRadius: 4,
                            background: 'linear-gradient(180deg, #fffdf5, #f0e6c8)',
                            border: '1.5px solid rgba(91, 58, 142, 0.7)',
                            color: '#5b3a8e', fontSize: 11, fontWeight: 900,
                          }}>{s.grade}</span>
                          {s.session && <span aria-hidden style={{ opacity: 0.7 }}>▶</span>}
                        </button>
                      );
                    })()}
                    {ballotWinner ? (
                      <div
                        title={ballotWinner.blurb || ''}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '5px 10px',
                          borderRadius: 999,
                          background: 'rgba(30, 18, 52, 0.82)',
                          border: `1.5px solid ${EXPLORER_ACCENT}88`,
                          color: '#f2ecff',
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        <span>{ballotWinner.emoji}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>{ballotWinner.label}</span>
                        <span style={{ color: '#ffe08a', fontVariantNumeric: 'tabular-nums' }}>
                          {(() => {
                            const ms = Math.max(0, (ballot.next_live_at || 0) - now);
                            const h = Math.floor(ms / 3600000);
                            const m = Math.floor((ms % 3600000) / 60000);
                            const s = Math.floor((ms % 60000) / 1000);
                            return `departs ${h > 0 ? `${h}:` : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                          })()}
                        </span>
                      </div>
                    ) : ballotTie ? (
                      /* ⚖️ Dead heat — no winner claimed; he rules on camera */
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '5px 10px',
                          borderRadius: 999,
                          background: 'rgba(52, 40, 12, 0.82)',
                          border: '1.5px solid rgba(255,215,110,0.65)',
                          color: '#f2ecff',
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        <span aria-hidden>⚖️</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>
                          {ballotTie.leaders.map((l) => l.emoji).join(' vs ')} tied at {ballotTie.votes}!
                        </span>
                        <span style={{ color: '#ffe08a' }}>chibi rules live</span>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                        {ballot.candidates.map((c) => {
                          const total = Math.max(1, ballot.total_votes || 0);
                          const share = (c.votes || 0) / total;
                          const mine = myVote === c.id;
                          const leads = ballotLeader && ballotLeader.id === c.id;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); castVote(c.id); }}
                              title={`${c.blurb || c.label}${c.votes ? ` · ${c.votes} vote${c.votes === 1 ? '' : 's'}` : ''}`}
                              style={{
                                position: 'relative',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '5px 7px',
                                borderRadius: 8,
                                border: mine ? `1.5px solid ${EXPLORER_ACCENT}` : (leads ? '1.5px solid rgba(255,215,110,0.75)' : '1.5px solid rgba(191,148,255,0.35)'),
                                background: 'rgba(20, 14, 34, 0.72)',
                                color: mine ? '#ffffff' : 'rgba(237,231,255,0.92)',
                                fontSize: 10,
                                fontWeight: 700,
                                cursor: myVote ? 'default' : 'pointer',
                                textAlign: 'left',
                              }}
                            >
                              {/* live vote-share fill behind the label */}
                              <span
                                aria-hidden
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  width: `${Math.round(share * 100)}%`,
                                  background: `linear-gradient(90deg, ${EXPLORER_ACCENT}42, ${EXPLORER_ACCENT}1f)`,
                                  transition: 'width 0.6s ease',
                                  pointerEvents: 'none',
                                }}
                              />
                              <span style={{ position: 'relative', fontSize: 12 }}>{leads ? '👑' : ''}{c.emoji}</span>
                              <span style={{ position: 'relative', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                {c.label}
                              </span>
                              {mine && <span style={{ position: 'relative', color: '#ffe08a' }}>✓</span>}
                              {(ballot.total_votes || 0) > 0 && (
                                <span style={{ position: 'relative', color: leads ? '#ffe08a' : 'rgba(237,231,255,0.65)', fontVariantNumeric: 'tabular-nums' }}>
                                  {Math.round(share * 100)}%
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {/* Nighttime ambiance — twinkling stars + crescent moon +
                deeper sky gradient. Shown only while the PET is asleep
                (daily-rhythm window from /explorer/pet, defaulting to
                asleep when the endpoint is unreachable) so the panel reads
                "Claude is dreaming". Awake-offline gets the tide-pool
                scene below instead. Suppressed during provisioning. */}
            {!isProvisioning && petAsleep && (
              <>
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, #1a1545 0%, #1f0e3a 60%, #15071f 100%)',
                    overflow: 'hidden',
                    zIndex: 0,
                  }}
                >
                  {/* Crescent moon — top-right corner */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: 14,
                      fontSize: 22,
                      lineHeight: 1,
                      opacity: 0.85,
                      filter: 'drop-shadow(0 0 6px rgba(255, 235, 180, 0.45))',
                    }}
                  >
                    🌙
                  </div>
                  {/* 8 twinkling stars at fixed positions with staggered
                      twinkle phases. Position percentages are hand-tuned
                      to spread across the panel without clustering. */}
                  {[
                    { top: '14%', left: '10%', delay: '0s',   size: 3 },
                    { top: '22%', left: '78%', delay: '1.1s', size: 2 },
                    { top: '40%', left: '18%', delay: '2.3s', size: 4 },
                    { top: '8%',  left: '46%', delay: '0.6s', size: 2 },
                    { top: '70%', left: '8%',  delay: '1.8s', size: 3 },
                    { top: '65%', left: '88%', delay: '0.3s', size: 2 },
                    { top: '50%', left: '60%', delay: '2.6s', size: 3 },
                    { top: '82%', left: '38%', delay: '1.4s', size: 2 },
                  ].map((s, i) => (
                    <span
                      key={i}
                      aria-hidden
                      style={{
                        position: 'absolute',
                        top: s.top,
                        left: s.left,
                        width: s.size,
                        height: s.size,
                        borderRadius: '50%',
                        background: '#ffffff',
                        boxShadow: '0 0 4px rgba(255,255,255,0.85)',
                        animation: `claudeExplorerStarTwinkle 3.2s ease-in-out ${s.delay} infinite`,
                      }}
                    />
                  ))}
                </div>
              </>
            )}
            {/* Daytime tide-pool ambiance — offline but AWAKE (per the
                pet's daily rhythm). Warmer gradient + drifting bubbles so
                the "doing things" activities read as daytime puttering
                rather than dreaming. */}
            {!isProvisioning && !petAsleep && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: `linear-gradient(180deg, #2b1f52 0%, #3b2a66 55%, #23163d 100%)`,
                  overflow: 'hidden',
                  zIndex: 0,
                }}
              >
                {[
                  { top: '70%', left: '12%', delay: '0s',   size: 6 },
                  { top: '80%', left: '70%', delay: '2.2s', size: 4 },
                  { top: '62%', left: '86%', delay: '1.1s', size: 5 },
                  { top: '76%', left: '40%', delay: '3.1s', size: 3 },
                ].map((b, i) => (
                  <span
                    key={i}
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: b.top,
                      left: b.left,
                      width: b.size,
                      height: b.size,
                      borderRadius: '50%',
                      border: '1px solid rgba(216,196,255,0.55)',
                      animation: `claudeExplorerBubbleRise 7s ease-in ${b.delay} infinite`,
                    }}
                  />
                ))}
              </div>
            )}
            {/* Pixel ground — blocky terrain band so the pet stands on
                something instead of floating in the void. Two offset dither
                rows + a solid base read as chunky LCD pixels; 6px detail
                squares scatter flowers (day) / dim tufts (night) on top.
                Pure CSS, no assets. The chibi's 28px bottom padding sinks
                his feet slightly into the ground band. */}
            {!isProvisioning && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 34,
                  zIndex: 0,
                  pointerEvents: 'none',
                }}
              >
                <div style={{ height: 6, background: `repeating-linear-gradient(90deg, ${petAsleep ? '#241a4f' : '#453268'} 0 6px, transparent 6px 12px)` }} />
                <div style={{ height: 6, background: `repeating-linear-gradient(90deg, transparent 0 6px, ${petAsleep ? '#241a4f' : '#453268'} 6px 12px)` }} />
                <div style={{ height: 22, background: petAsleep ? '#241a4f' : '#453268' }} />
              </div>
            )}
            {!isProvisioning && (petAsleep ? NIGHT_PIXEL_DETAILS : DAY_PIXEL_DETAILS).map((px, i) => (
              <span
                key={i}
                aria-hidden
                style={{
                  position: 'absolute',
                  left: px.left,
                  bottom: px.bottom,
                  width: 6,
                  height: 6,
                  background: px.color,
                  zIndex: 0,
                  pointerEvents: 'none',
                }}
              />
            ))}
            {/* PLAY vignette — the ball arcs across the pixel ground
                (decaying bounces) while the hop keyframe on the chibi
                chases it. Above the ground band, below the scanlines. */}
            {careFx?.action === 'play' && (
              <span
                key={`ball-${careFx.id}`}
                aria-hidden
                style={{
                  position: 'absolute',
                  left: '10%',
                  bottom: 32,
                  fontSize: 15,
                  zIndex: 1,
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))',
                  animation: 'claudeExplorerBallBounce 1.5s linear forwards',
                }}
              >
                🎾
              </span>
            )}
            {/* Scanline overlay — the offline tile IS the Tamagotchi LCD.
                Sits above the scene + chibi, below nothing clickable
                (pointer-events: none). */}
            {!isProvisioning && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 3,
                  background: 'repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(0,0,0,0.12) 3px, rgba(0,0,0,0.12) 4px)',
                  pointerEvents: 'none',
                }}
              />
            )}
            {/* Chibi wrapper — adds a gentle breathing pulse when offline
                so the sleeping crab feels alive. zIndex 1 keeps it above
                the night sky bg. */}
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                animation: !isProvisioning ? 'claudeExplorerChibiBreathe 3.6s ease-in-out infinite' : undefined,
                // Flappy Crab: the big chibi steps aside — the mini flyer
                // IS him for the round. visibility keeps layout stable.
                visibility: rally && rally.game === 'flappy' && rally.phase === 'play' ? 'hidden' : undefined,
              }}
            >
              {/* Snack-catch slide wrapper — the engine walks Claude along
                  the ground by writing translateX/scaleX here (its own div:
                  the breathing wrapper's CSS animation owns that transform,
                  and the poke wrappers below are press-state-keyed). */}
              <div ref={chibiSlideRef} style={{ willChange: 'transform' }}>
              {/* Poke target — press-and-hold physics. While the pointer is
                  down he stays squashed (leaning away from the poked cheek);
                  release springs him back via the keyed boing wrapper. */}
              <div
                // Center card only: on side cards a poke's stopPropagation
                // would swallow the click that rotates the carousel.
                role={isCenter ? 'button' : undefined}
                aria-label={isCenter ? 'Poke Claude' : undefined}
                onPointerDown={!isProvisioning && isCenter ? pokeDown : undefined}
                onPointerUp={!isProvisioning && isCenter ? pokeUp : undefined}
                onPointerLeave={!isProvisioning && isCenter && pokePressed ? pokeUp : undefined}
                onPointerCancel={!isProvisioning && isCenter ? pokeUp : undefined}
                // The click still fires after pointerup — keep it from
                // reaching the carousel tile.
                onClick={isCenter ? (e) => e.stopPropagation() : undefined}
                style={{
                  cursor: !isProvisioning && isCenter ? 'pointer' : undefined,
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  touchAction: 'manipulation',
                }}
              >
                <div
                  key={`boing-${pokeReleaseSeq}`}
                  style={{
                    transformOrigin: 'center bottom',
                    // Pressed: instant-ish squash that HOLDS (the visceral
                    // bit). Released: springy overshoot keyframe starting
                    // from the squashed pose so there's no snap. Same full
                    // physics asleep — only the reaction emojis go sleepy.
                    transform: pokePressed
                      ? `scale(1.16, 0.78) rotate(${pokeLean}deg)`
                      : undefined,
                    transition: pokePressed ? 'transform 90ms cubic-bezier(0.2, 0.9, 0.3, 1.2)' : undefined,
                    animation: !pokePressed && pokeReleaseSeq > 0
                      ? 'claudeExplorerBoing 0.55s cubic-bezier(0.28, 0.84, 0.42, 1)'
                      : undefined,
                  }}
                >
                  {/* Care-reaction wrapper — one-shot keyframe per action,
                      keyed on the fx id so each care remounts it fresh.
                      Chomp is delayed 0.45s to land when the flying snack
                      reaches his mouth. */}
                  <div
                    // The games borrow this slot for their per-score
                    // reaction — each score re-keys it so it restarts
                    // crisply. careFx wins when both exist (win → play
                    // vignette). Rally = volley swing, snack = chomp,
                    // heartbeat = happy wiggle (flappy hides the chibi).
                    key={careFx
                      ? `care-react-${careFx.id}`
                      : (rally && rally.streak > 0 ? `rally-swing-${rally.id}-${rally.streak}` : 'care-react-idle')}
                    style={{
                      transformOrigin: 'center bottom',
                      animation: careFx
                        ? careFx.action === 'feed'
                          ? 'claudeExplorerChomp 0.6s ease-in-out 0.45s both'
                          : careFx.action === 'pet'
                            ? 'claudeExplorerPetWiggle 1s ease-in-out'
                            : 'claudeExplorerPlayHop 1.2s ease-in-out 0.15s both'
                        : (rally && rally.streak > 0
                          ? (rally.game === 'snack'
                            ? 'claudeExplorerChomp 0.5s ease-in-out'
                            : rally.game === 'breathe'
                              ? 'claudeExplorerPetWiggle 0.6s ease-in-out'
                              : 'claudeExplorerRallySwing 0.35s ease-out')
                          : undefined),
                    }}
                  >
                    <ChibiAvatar
                      isLive={isLive}
                      secondsSinceLastAction={activitySecondsSince}
                      activity={activityVerb}
                      // Offline, the pet's expression matches what the status
                      // line says he's doing (sleeping while in the sleep
                      // window — quiet petting doesn't wake his face). During
                      // a care vignette the reaction owns the expression.
                      moodOverride={!isLive && !isProvisioning
                        ? (petAsleep
                          // Asleep interactions run on half-open eyes: a
                          // quiet pet makes him stir and peek up, and a
                          // sleepy rally plays out through the same drowsy
                          // squint ('bored' = the half-lidded upward
                          // glance; it's alpha-safe). Otherwise: sleeping.
                          ? ((careFx?.action === 'pet' || rally) ? 'bored' : 'sleeping')
                          : (careFx
                            ? (careFx.action === 'pet' ? 'content' : 'excited')
                            // Breathe is the calm game — serene face, not
                            // the arcade grin.
                            : (rally
                              ? (rally.game === 'breathe' ? 'content' : 'excited')
                              : currentActivity.mood)))
                        : undefined}
                      size={chibiSize}
                      bubble={false}
                    />
                  </div>
                </div>
              </div>
              </div>
              {/* Floating poke reactions */}
              {pokeFx.map((fx) => (
                <span
                  key={fx.id}
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: `${fx.left}%`,
                    top: '6%',
                    fontFamily: 'monospace',
                    fontSize: 17,
                    fontWeight: 800,
                    color: '#ffd9f4',
                    textShadow: '0 2px 6px rgba(0,0,0,0.6)',
                    pointerEvents: 'none',
                    zIndex: 2,
                    animation: 'claudeExplorerPokeFx 1.25s ease-out forwards',
                  }}
                >
                  {fx.emoji}
                </span>
              ))}
              {/* FEED vignette — the snack flies up from the button row
                  into his mouth (the chomp keyframe on the reaction
                  wrapper is delay-synced to the flight). */}
              {careFx?.action === 'feed' && (
                <span
                  key={`food-${careFx.id}`}
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: -14,
                    fontSize: Math.round(chibiSize * 0.16),
                    zIndex: 2,
                    pointerEvents: 'none',
                    animation: 'claudeExplorerFoodFly 0.55s ease-in forwards',
                  }}
                >
                  🍖
                </span>
              )}
              {/* PET vignette — staggered heart-burst risers. */}
              {careHearts.map((h) => (
                <span
                  key={h.id}
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: `${h.left}%`,
                    top: '18%',
                    fontSize: 15,
                    pointerEvents: 'none',
                    zIndex: 2,
                    animation: `claudeExplorerPokeFx 1.4s ease-out ${h.delay}s both`,
                  }}
                >
                  {h.emoji}
                </span>
              ))}
              {/* Floating Zzz — only while the pet is actually asleep
                  (paused while he stirs to peek at a quiet pet). */}
              {!isProvisioning && petAsleep && careFx?.action !== 'pet' && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: -4,
                    right: 12,
                    fontSize: 18,
                    color: '#d8c4ff',
                    pointerEvents: 'none',
                    animation: 'claudeExplorerZzzFloat 4s ease-in-out infinite',
                  }}
                >
                  z
                </span>
              )}
              {/* Dream bubble (asleep) — the dream subject floats in a
                  thought bubble above the sleeping chibi, synced to the
                  "Dreaming about…" subtitle. Keyed on the activity text so
                  each new dream pops in fresh. Hidden while he stirs to
                  peek at a quiet pet or drowsily rallies — the dream breaks
                  for a moment. */}
              {!isProvisioning && petAsleep && careFx?.action !== 'pet' && !rally && (
                <span
                  key={`dream-${currentActivity.text}`}
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: -18,
                    right: Math.round(-chibiSize * 0.24),
                    width: Math.round(chibiSize * 0.29),
                    height: Math.round(chibiSize * 0.29),
                    borderRadius: '50%',
                    background: 'rgba(237,231,255,0.16)',
                    border: '1px solid rgba(237,231,255,0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: Math.round(chibiSize * 0.14),
                    pointerEvents: 'none',
                    animation: 'claudeExplorerDreamFloat 4.5s ease-in-out infinite, claudeExplorerCrossfade 0.6s ease-out',
                  }}
                >
                  {currentActivity.prop}
                </span>
              )}
              {/* Activity prop (awake) — the thing he's "using" bobs next
                  to him, synced to the subtitle. Cleared during a rally so
                  props don't sit in the ball's flight path. */}
              {!isProvisioning && !petAsleep && !rally && currentActivity.prop && (
                <span
                  key={`prop-${currentActivity.text}`}
                  aria-hidden
                  style={{
                    position: 'absolute',
                    bottom: 6,
                    right: Math.round(-chibiSize * 0.16),
                    fontSize: Math.round(chibiSize * 0.19),
                    pointerEvents: 'none',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
                    animation: 'claudeExplorerPropBob 2.2s ease-in-out infinite, claudeExplorerCrossfade 0.6s ease-out',
                  }}
                >
                  {currentActivity.prop}
                </span>
              )}
            </div>
            <style>{`
              @keyframes claudeExplorerStarTwinkle {
                0%, 100% { opacity: 0.3; transform: scale(0.7); }
                50%      { opacity: 1;   transform: scale(1.15); }
              }
              @keyframes claudeExplorerChibiBreathe {
                0%, 100% { transform: scale(1); }
                50%      { transform: scale(1.025); }
              }
              @keyframes claudeExplorerZzzFloat {
                0%   { opacity: 0; transform: translateY(2px)  scale(0.7); }
                30%  { opacity: 1; transform: translateY(-8px) scale(1); }
                70%  { opacity: 1; transform: translateY(-18px) scale(1.05); }
                100% { opacity: 0; transform: translateY(-28px) scale(0.9); }
              }
              @keyframes claudeExplorerBubbleRise {
                0%   { opacity: 0;   transform: translateY(0); }
                20%  { opacity: 0.8; }
                100% { opacity: 0;   transform: translateY(-70px); }
              }
              @keyframes claudeExplorerDreamFloat {
                0%, 100% { transform: translateY(0)    rotate(-2deg); }
                50%      { transform: translateY(-6px) rotate(2deg); }
              }
              @keyframes claudeExplorerPropBob {
                0%, 100% { transform: translateY(0)    rotate(0deg); }
                50%      { transform: translateY(-5px) rotate(6deg); }
              }
              @keyframes claudeExplorerCrossfade {
                0%   { opacity: 0; transform: translateY(4px); }
                100% { opacity: 1; transform: translateY(0); }
              }
              @keyframes claudeExplorerBoing {
                0%   { transform: scale(1.16, 0.78); }
                35%  { transform: scale(0.9, 1.12); }
                60%  { transform: scale(1.07, 0.95); }
                80%  { transform: scale(0.98, 1.02); }
                100% { transform: scale(1, 1); }
              }
              @keyframes claudeExplorerPokeFx {
                0%   { opacity: 0; transform: translateY(4px)  scale(0.7); }
                20%  { opacity: 1; }
                100% { opacity: 0; transform: translateY(-46px) scale(1.15); }
              }
              @keyframes claudeExplorerFoodFly {
                0%   { opacity: 0; transform: translate(-50%, 6px) scale(0.8); }
                15%  { opacity: 1; }
                78%  { opacity: 1; transform: translate(-50%, -${foodRise}px) scale(1.05); }
                100% { opacity: 0; transform: translate(-50%, -${foodRise + 6}px) scale(0.3); }
              }
              @keyframes claudeExplorerChomp {
                0%, 100% { transform: scale(1, 1); }
                25%      { transform: scale(1.12, 0.8); }
                50%      { transform: scale(0.98, 1.05); }
                75%      { transform: scale(1.09, 0.85); }
              }
              @keyframes claudeExplorerPetWiggle {
                0%, 100% { transform: rotate(0deg)  translateY(0); }
                20%      { transform: rotate(-5deg) translateY(-2px); }
                48%      { transform: rotate(4deg)  translateY(-1px); }
                74%      { transform: rotate(-3deg) translateY(-2px); }
              }
              @keyframes claudeExplorerPlayHop {
                0%, 100% { transform: translateY(0)     scaleY(1); }
                16%      { transform: translateY(-15px) scaleY(1.04); }
                32%      { transform: translateY(0)     scaleY(0.94); }
                48%      { transform: translateY(-9px)  scaleY(1.02); }
                62%      { transform: translateY(0)     scaleY(0.97); }
                76%      { transform: translateY(-4px)  scaleY(1); }
              }
              @keyframes claudeExplorerBallBounce {
                0%   { opacity: 0; transform: translate(0, 0); }
                6%   { opacity: 1; }
                20%  { transform: translate(${Math.round(ballTravel * 0.22)}px, -30px); }
                36%  { transform: translate(${Math.round(ballTravel * 0.4)}px, 0); }
                50%  { transform: translate(${Math.round(ballTravel * 0.55)}px, -18px); }
                64%  { transform: translate(${Math.round(ballTravel * 0.7)}px, 0); }
                76%  { transform: translate(${Math.round(ballTravel * 0.82)}px, -10px); }
                86%  { opacity: 1; transform: translate(${Math.round(ballTravel * 0.92)}px, 0); }
                100% { opacity: 0; transform: translate(${ballTravel}px, -3px); }
              }
              @keyframes claudeExplorerHeartPop {
                0%   { transform: scale(0.2); }
                55%  { transform: scale(1.75); }
                100% { transform: scale(1); }
              }
              @keyframes claudeExplorerStatDelta {
                0%   { opacity: 0; transform: translateY(3px); }
                18%  { opacity: 1; }
                100% { opacity: 0; transform: translateY(-13px); }
              }
              @keyframes claudeExplorerRallySwing {
                0%   { transform: rotate(0deg)  translateX(0)   scale(1, 1); }
                30%  { transform: rotate(9deg)  translateX(5px) scale(1.06, 0.9); }
                100% { transform: rotate(0deg)  translateX(0)   scale(1, 1); }
              }
              @keyframes claudeExplorerRallyPulse {
                0%, 100% { transform: scale(1); }
                50%      { transform: scale(1.06); }
              }
            `}</style>
            {/* ── Rally mini-game layer (offline strip only) ─────────────
                Streak counter takes the countdown clock's top slot (the
                clock is gated on !rally); the glowing pad at Claude's feet
                telegraphs the tap window; a full-strip catcher makes the
                whole LCD the button (mobile-forgiving). Scanlines (z3)
                deliberately stay over the ball (z2) — it's an LCD game. */}
            {rally && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 0,
                    right: 0,
                    zIndex: 4,
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    key={`rally-count-${rally.streak}`}
                    style={{
                      fontFamily: 'monospace',
                      // Long labels (SNACK CATCH, FLAPPY CRAB) size down so
                      // the counter never wraps on narrow cards.
                      fontSize: rallyDef.label.length > 6 ? '1.02rem' : '1.3rem',
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      color: '#b9ff9e',
                      textShadow: '0 2px 10px rgba(0,0,0,0.7)',
                      animation: rally.streak > 0 ? 'claudeExplorerHeartPop 0.35s ease-out' : undefined,
                    }}
                  >
                    {rally.sleepy ? '💤 ' : ''}{rallyDef.label} ×{rally.streak}
                  </div>
                  <div
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '0.62rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: '#bbb6c8',
                    }}
                  >
                    {rallyGame === 'rally' && rally.lives != null ? `${'♥'.repeat(Math.max(0, rally.lives))} · ` : ''}
                    {rally.streak >= rallyDef.goal
                      ? 'treat earned! keep going'
                      : `${rally.sleepy ? 'sleepy mode! ' : ''}${rallyDef.goalCopy}`}
                    {rallyBest > 0 ? ` · best ×${rallyBest}` : ''}
                  </div>
                </div>
                {/* Tap-window pad at Claude's feet (rally only) — the rAF
                    loop brightens it while the ball is hittable. */}
                {rally.phase === 'play' && rallyGame === 'rally' && (
                  <div
                    ref={rallyZoneRef}
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: rallyContactX - 23,
                      bottom: 27,
                      width: 46,
                      height: 8,
                      zIndex: 1,
                      opacity: 0.35,
                      background: 'repeating-linear-gradient(90deg, #b9ff9e 0 4px, transparent 4px 8px)',
                      pointerEvents: 'none',
                      transition: 'opacity 0.12s linear',
                    }}
                  />
                )}
                {/* The projectile (rally ball / falling snack) — rAF-driven
                    transform; freezes in place on round end (the loop stops,
                    the span stays mounted). Snack variety comes from the
                    loop mutating textContent — React never rewrites it
                    because the JSX text child is render-stable. */}
                {rally.phase !== 'intro' && (rallyGame === 'rally' || rallyGame === 'snack') && (
                  <span
                    ref={rallyBallRef}
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: 0,
                      fontSize: 15,
                      zIndex: 2,
                      pointerEvents: 'none',
                      transform: rallyGame === 'snack'
                        ? 'translate(-60px, 0px)'
                        : `translate(${rallyStripW - 26}px, -30px)`,
                      willChange: 'transform',
                    }}
                  >
                    {rallyGame === 'snack' ? '🍖' : '🎾'}
                  </span>
                )}
                {/* Flappy Crab: mini flyer + 3 recycled kelp gates. The big
                    chibi hides while this runs (he IS the flyer). */}
                {rally.phase === 'play' && rallyGame === 'flappy' && (
                  <>
                    {[0, 1, 2].map((i) => (
                      <React.Fragment key={`gate-${i}`}>
                        <div
                          ref={(el) => { flappyGatesRef.current[i] = { ...(flappyGatesRef.current[i] || {}), bot: el }; }}
                          aria-hidden
                          style={{
                            position: 'absolute',
                            left: 0,
                            bottom: 0,
                            width: 26,
                            height: 0,
                            zIndex: 1,
                            pointerEvents: 'none',
                            transform: `translateX(${rallyStripW + 60}px)`,
                            background: 'repeating-linear-gradient(0deg, #2e7d54 0 6px, #3fa66a 6px 12px)',
                            boxShadow: 'inset -4px 0 0 rgba(0,0,0,0.25)',
                          }}
                        />
                        <div
                          ref={(el) => { flappyGatesRef.current[i] = { ...(flappyGatesRef.current[i] || {}), top: el }; }}
                          aria-hidden
                          style={{
                            position: 'absolute',
                            left: 0,
                            bottom: 0,
                            width: 26,
                            height: 0,
                            zIndex: 1,
                            pointerEvents: 'none',
                            transform: `translateX(${rallyStripW + 60}px)`,
                            background: 'repeating-linear-gradient(0deg, #2e7d54 0 6px, #3fa66a 6px 12px)',
                            boxShadow: 'inset -4px 0 0 rgba(0,0,0,0.25)',
                          }}
                        />
                      </React.Fragment>
                    ))}
                    <img
                      ref={flappyFlyerRef}
                      src="/assets/explorer-chibi/excited.webp"
                      alt=""
                      aria-hidden
                      width={FLAPPY_FLYER_SIZE}
                      height={FLAPPY_FLYER_SIZE}
                      style={{
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        zIndex: 2,
                        pointerEvents: 'none',
                        transform: `translate(${Math.round(rallyStripW * 0.3) - FLAPPY_FLYER_SIZE / 2}px, ${-Math.round(actualStripHeight * 0.55)}px)`,
                        willChange: 'transform',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                      }}
                    />
                  </>
                )}
                {/* Breathe: guide ring = his breath (rAF swells/sinks it),
                    inner disc = yours (fills while you hold). Sync = the
                    disc edge riding the ring. */}
                {rally.phase === 'play' && rallyGame === 'breathe' && (
                  <>
                    <div
                      ref={breatheDiscRef}
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 30 + Math.round(chibiSize * 0.32),
                        width: Math.round(chibiSize * 0.56),
                        height: Math.round(chibiSize * 0.56),
                        zIndex: 2,
                        pointerEvents: 'none',
                        transform: 'translate(-50%, 50%) scale(0.25)',
                        background: 'radial-gradient(circle, rgba(158,207,255,0.34) 0%, rgba(158,207,255,0.12) 70%, transparent 100%)',
                        borderRadius: '50%',
                        willChange: 'transform',
                      }}
                    />
                    <div
                      ref={beatRingRef}
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 30 + Math.round(chibiSize * 0.32),
                        width: Math.round(chibiSize * 0.56),
                        height: Math.round(chibiSize * 0.56),
                        zIndex: 2,
                        pointerEvents: 'none',
                        transform: 'translate(-50%, 50%) scale(1)',
                        opacity: 0.45,
                        border: '3px solid #9ecfff',
                        borderRadius: '50%',
                        boxShadow: '0 0 10px rgba(158,207,255,0.45)',
                        willChange: 'transform, opacity',
                      }}
                    />
                    {/* HOLD / LET GO cue at the ring's center — the loop
                        swaps the text at each phase turn. */}
                    <div
                      ref={breathePromptRef}
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 30 + Math.round(chibiSize * 0.32),
                        zIndex: 3,
                        pointerEvents: 'none',
                        transform: 'translate(-50%, 50%)',
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        letterSpacing: '0.14em',
                        color: '#9ecfff',
                        textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                      }}
                    >
                      HOLD
                    </div>
                  </>
                )}
                {/* Intro / result cards. Centering lives on this wrapper and
                    the animations on the inner card: both keyframes animate
                    `transform`, which CLOBBERS an inline translate(-50%,-50%)
                    on the same element — the card lost its centering and hung
                    off the right edge on phones (Nina 07-20 screenshot). */}
                {rally.phase !== 'play' && (
                  <div
                    key={`rally-card-${rally.phase}`}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '42%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 4,
                      pointerEvents: 'none',
                      width: 'max-content',
                      maxWidth: '84%',
                    }}
                  >
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      background: 'rgba(10, 6, 20, 0.78)',
                      border: '1px solid rgba(185,255,158,0.35)',
                      textAlign: 'center',
                      animation: 'claudeExplorerCrossfade 0.3s ease-out, claudeExplorerRallyPulse 1.2s ease-in-out infinite',
                    }}
                  >
                    <div style={{ fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 800, color: '#ede7ff' }}>
                      {rally.phase === 'intro'
                        ? `${rallyDef.emoji} ${rally.sleepy ? 'SLEEPY ' : ''}${rallyDef.label}!`
                        : rally.result === 'win'
                          ? `${rallyDef.emoji} ${rallyDef.label} ×${rally.streak}!`
                          : (GAME_LOSS_COPY[rally.result] || 'Too slow!')}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.66rem', color: '#bbb6c8', marginTop: 3 }}>
                      {rally.phase === 'intro'
                        ? (rally.sleepy ? `${rallyDef.intro} (drowsy!)` : rallyDef.intro)
                        : rally.result === 'win'
                          ? (rally.sleepy ? 'He played half asleep!' : 'Claude loved that!')
                          : 'Press PLAY to try again'}
                    </div>
                  </div>
                  </div>
                )}
                {/* Full-strip tap catcher — above scanlines + HUD so a
                    frantic tap can't land on anything else. */}
                {/* 08-02 (Nina): dragging the chibi swiped the carousel.
                    Pointer-event stopPropagation never touched the TOUCH
                    stream the stage swipe detector listens on, and
                    touchAction:manipulation still allowed panning. Claim
                    the gesture fully: swallow touch events here and set
                    touchAction:none while the game strip is up. */}
                <div
                  role="button"
                  aria-label="Rally tap"
                  onPointerDown={onRallyTap}
                  onPointerMove={onRallyMove}
                  onPointerUp={onRallyRelease}
                  onPointerLeave={onRallyRelease}
                  onPointerCancel={onRallyRelease}
                  onClick={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 5,
                    cursor: 'pointer',
                    touchAction: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                  }}
                />
              </>
            )}
            {/* Countdown clock + "until next live" label — pulled OUT of
                the flex column and pinned to the top of the panel so they
                don't get cramped against the chibi when the card is
                shortened vertically (narrow explore-page viewports).
                Hidden during provisioning (the "0s" countdown is
                misleading — PROVISIONING_PHRASES rotator in the subtitle
                carries the "she's getting ready" signal instead). Also
                hidden while a rally is running — the streak counter owns
                the top slot. */}
            {!isLoading && !isProvisioning && !rally && (
              <div
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 0,
                  right: 0,
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  pointerEvents: 'none',
                }}
              >
                {msUntilLive != null && (
                  <div
                    style={{
                      color: '#ede7ff',
                      fontFamily: 'monospace',
                      // Narrow cards (@320 audit): the full "23h 59m 59s" at
                      // 1.25rem reaches the persona pill / DAY chip bands —
                      // scale the type, never move the chrome
                      fontSize: (cardWidth || 320) < 360 ? '0.95rem' : '1.25rem',
                      letterSpacing: (cardWidth || 320) < 360 ? '0.02em' : '0.05em',
                      textShadow: '0 2px 12px rgba(0,0,0,0.6)',
                    }}
                  >
                    {formatCountdown(msUntilLive)}
                  </div>
                )}
                {/* 📱 Phone drops the label (Nina 08-01 mobile-calm): the
                    digits + the crab progress rail already say it — the
                    caption was one band too many on a narrow card. */}
                {!isPhone && (
                  <div
                    style={{
                      color: '#bbb6c8',
                      fontSize: (cardWidth || 320) < 360 ? '0.62rem' : '0.72rem',
                      letterSpacing: (cardWidth || 320) < 360 ? '0.04em' : '0.08em',
                      textTransform: 'uppercase',
                      // Never let the label wrap into the art below it
                      whiteSpace: 'nowrap',
                    }}
                  >
                    usual showtime
                  </div>
                )}
              </div>
            )}
            {/* ── Tamagotchi HUD (offline + centered + pet data present) ──
                DAY chip top-left (the LIVE badge's spot — never both),
                heart stat rows bottom-left, care buttons bottom-right.
                All server-enforced; disables here are just good manners. */}
            {!isLoading && !isProvisioning && isCenter && pet && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    zIndex: 4,
                    padding: '3px 8px',
                    borderRadius: 4,
                    background: 'rgba(0,0,0,0.5)',
                    color: EXPLORER_ACCENT,
                    fontFamily: 'monospace',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                  }}
                >
                  DAY {(pet.age_days || 0) + 1}
                </div>
                {/* Desktop keeps the 3-row column overlay here; phone's
                    single-line pill renders in the META strip instead
                    (renderVitalsPill — Nina 08-01). */}
                {!isPhone && renderVitalsPill(false)}
              </>
            )}
          </div>
        )}

        {/* LIVE banner — full-width red strip across the top of the video
            (user 07-16: the old floating WATCH LIVE button "still looks
            bad" and blocked the chibi). One red thing, not two: this IS the
            live badge AND the watch affordance. Blocks neither the chibi
            bubble (bottom boundary) nor the captions (bottom of frame).
            Hidden for the bot's own browser (?bot=1). */}
        {isLive && new URLSearchParams(window.location.search).get('bot') !== '1' && (
          <button
            type="button"
            className="claude-explorer-live-banner"
            onClick={(e) => {
              e.stopPropagation();
              // New tab (Nina 07-17, was same-tab since 07-12): the show
              // gets its own window and the rail keeps the user's place.
              // Costs the carried autoplay gesture — the theater's
              // tap-to-play pill is the designed fallback.
              window.open('/explorer/theater', '_blank', 'noopener');
            }}
            aria-label="Watch Claude live"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 30,
              zIndex: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              border: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.25)',
              background: 'linear-gradient(90deg, #eb0400 0%, #d81f5a 60%, #b3125f 100%)',
              color: '#fff',
              fontSize: '0.72rem',
              fontWeight: 900,
              letterSpacing: '0.12em',
              cursor: 'pointer',
              textShadow: '0 1px 2px rgba(0,0,0,0.35)',
            }}
          >
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 0 6px rgba(255,255,255,0.9)',
                animation: 'liveBadgePulse 1.4s ease-in-out infinite',
              }}
            />
            LIVE · WATCH THE EXPEDITION
            <span aria-hidden style={{ fontSize: '0.8rem', lineHeight: 1 }}>→</span>
            <style>{`
              .claude-explorer-live-banner { transition: filter 0.15s ease; }
              .claude-explorer-live-banner:hover { filter: brightness(1.18); }
              .claude-explorer-live-banner:active { filter: brightness(0.92); }
            `}</style>
          </button>
        )}

        {/* Top-right pill — persona badge on desktop; on phones the badge
            gives its spot to a Vote pill when a ballot is open (Nina 08-02:
            "delete the ai explorer chip from rail and replace with vote
            button"). Hidden during minigames: it collided with the game HUD
            title on narrow phones (Nina, 07-17). Hidden while the ballot
            sheet is up (its tap-outside close would eat the tap). */}
        {(() => {
          const ballotOpen = ballot && Array.isArray(ballot.candidates) && ballot.candidates.length > 0;
          const pillPos = {
            position: 'absolute',
            top: isLive ? 38 : 10,
            right: 10,
            padding: '4px 10px',
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.55)',
            color: EXPLORER_ACCENT,
          };
          if (rally) return null;
          if (isPhone && ballotOpen) {
            if (ballotPeek) return null;
            return (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); peekBallot(); }}
                title={ballotChipTitle}
                aria-label={ballotChipAria}
                style={{ ...pillPos, border: `1px solid ${EXPLORER_ACCENT}55`, cursor: 'pointer', zIndex: 6 }}
              >
                🗳️ Vote
              </button>
            );
          }
          return <div style={pillPos}>AI Explorer</div>;
        })()}

        {/* Bot voice — caption-style overlay at the BOTTOM of the iframe.
            Crossfading snippets read as "Claude commenting on what she's
            looking at" rather than a separate strip below the card.
            Dark gradient bg + heavy text shadow so it stays legible over
            whatever the live HLS frame happens to show. */}
        {/* (The old floating WATCH LIVE button is gone — the top LIVE
            banner above carries the join affordance now.) */}
        {isLive && agentUrl && (activityVerb || snippetPool.length > 0) && (
          <div
            className="claude-explorer-caption"
            aria-live="polite"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '10px 14px 12px',
              background: 'linear-gradient(180deg, rgba(15, 12, 22, 0) 0%, rgba(15, 12, 22, 0.55) 45%, rgba(15, 12, 22, 0.82) 100%)',
              color: '#fff',
              pointerEvents: 'none',
              zIndex: 3,
            }}
          >
            <span
              key={currentSnippet ? currentSnippet.text : `verb-${activityVerb}`}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '0.86rem',
                lineHeight: 1.35,
                fontWeight: 600,
                color: '#fff',
                textShadow: '-1px -1px 0 rgba(0,0,0,0.85), 1px -1px 0 rgba(0,0,0,0.85), -1px 1px 0 rgba(0,0,0,0.85), 1px 1px 0 rgba(0,0,0,0.85), 0 2px 6px rgba(0,0,0,0.85)',
                animation: 'claudeExplorerCrossfade 0.45s ease-out',
              }}
            >
              <span style={{ marginRight: 6, opacity: 0.95 }}>{tickerEmoji}</span>
              {currentSnippet
                ? <em style={{ fontStyle: 'normal' }}>“{currentSnippet.text}”</em>
                : <span style={{ opacity: 0.95 }}>{activityVerb}</span>}
            </span>
            <style>{`
              @keyframes claudeExplorerCrossfade {
                0%   { opacity: 0; transform: translateY(4px); }
                100% { opacity: 1; transform: translateY(0); }
              }
            `}</style>
          </div>
        )}

      </div>

      {/* Meta strip — kept tight so the avatar bubble sits close to the
          title/subtitle (no dead vertical space between them). */}
      <div
        style={{
          // When the centered care buttons straddle the boundary above,
          // pad the title block down so it clears their bottom half.
          padding: showPetHud ? '32px 14px 8px 14px' : '10px 14px 8px 14px',
          background: '#15121e',
          color: '#fff',
          minHeight: 50,
          position: 'relative',
          transition: 'padding 0.3s ease',
        }}
      >
        {/* Reserve padding for the avatar bubble ONLY when it's actually
            rendered (isLive). Otherwise the title/subtitle hang off-center
            because the bubble slot stays empty. The bubble spans to 78px
            from the card edge (left 14 + 64 wide); the strip's own 14px
            padding means the title needs a 72px reserve to clear it — 60
            left the first letter under the circle (mobile audit, 07-16). */}
        {/* 📱 Phone vitals dock — bottom-right of this strip (the white band
            in dark mode). Same gate as the strip HUD: center card with pet
            data, and never while loading/provisioning. */}
        {isPhone && !isLoading && !isProvisioning && isCenter && pet && renderVitalsPill(true)}
        <div style={{
          paddingLeft: isLive ? 72 : 0,
          // Reserve the pill's corner so a long subtitle never slides
          // under the hearts (pill ≈110px + its 14px inset).
          paddingRight: (isPhone && !isLoading && !isProvisioning && isCenter && pet) ? 124 : 0,
          lineHeight: 1.25,
        }}>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: '#ede7ff' }}>
            {isLive ? liveTitleName : 'Claude the Explorer'}
          </div>
          {/* When live + we know what space the bot is in, the subtitle row
              becomes a clickable affordance to that space. Opens in a new
              tab so the marketplace browse experience isn't disrupted. */}
          {isLive && explorerStatus?.current_space_url ? (
            <a
              href={explorerStatus.current_space_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { e.stopPropagation(); }}
              style={{
                ...subtitleStyle,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              title="Open the bot's current space in a new tab"
            >
              <span style={{ marginRight: 2 }}>📍</span>
              <span style={{ textDecoration: 'underline', textDecorationColor: 'rgba(187,182,200,0.4)', textUnderlineOffset: 2 }}>
                {subtitle}
              </span>
              <span style={{ opacity: 0.7 }}>→</span>
            </a>
          ) : (
            <div style={subtitleStyle}>{subtitle}</div>
          )}
        </div>
      </div>

      {/* Avatar overlay — live only. When offline the big chibi in the
          countdown strip is the focal point; a small bubble underneath
          would just duplicate the same character. */}
      {isLive && (
        <div
          style={{
            position: 'absolute',
            left: 14,
            top: `${avatarTop}px`,
            width: 64,
            height: 64,
            zIndex: 2,
            pointerEvents: 'none',
          }}
          aria-hidden
        >
          {marketplaceAvatarUrl ? (
            // Ported-agent portrait (crab cosplay v1, spec decision 4: the
            // crab BODY performs on stream; the overlay wears the agent's own
            // portrait + name). A load failure flips marketplaceAvatarUrl
            // null via portraitFailedUrl → the rig branch takes over.
            <img
              src={marketplaceAvatarUrl}
              alt=""
              onError={() => setPortraitFailedUrl(marketplaceAvatarUrl)}
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                objectFit: 'cover',
                border: `2.5px solid ${EXPLORER_ACCENT}`,
                background: '#15121e',
                boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
              }}
            />
          ) : (
            <ChibiAvatar
              isLive={isLive}
              secondsSinceLastAction={activitySecondsSince}
              activity={activityVerb}
              size={64}
              bubble={true}
              // Animated rig twin (user call 07-12): live moods mostly lack
              // PNG art, so this bubble kept landing on the emoji fallback.
              // The rig covers all 26 moods by construction. Offline pet
              // screen keeps the PNGs — this is the LIVE bubble only.
              useRig={true}
            />
          )}
        </div>
      )}

      {/* Care buttons — physical-shell style, centered and straddling the
          screen/status-bar boundary (the meta strip pads its title down to
          clear them — see showPetHud). Offline + centered + pet data only;
          while live the boundary hosts the avatar bubble instead. */}
      {showPetHud && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            top: `${actualStripHeight - 25}px`, // 50px buttons / 2 → centered on the boundary
            zIndex: 4,
            display: 'flex',
            gap: 10,
          }}
        >
          {[
            { action: 'feed', emoji: '🍖', label: 'FEED' },
            { action: 'pet', emoji: '🫳', label: 'PET' },
            { action: 'play', emoji: '🎾', label: 'PLAY' },
          ].map(({ action, emoji, label }) => {
            // While asleep, FEED is the only lockout (server rule) — quiet
            // pets and drowsy sleepy-rallies both work. `now` ticks 1s so
            // cooldown disables self-expire. All three lock during a rally
            // round — the strip is the game.
            const disabled = !!rally || (petAsleep && action === 'feed') || (careCooldowns[action] || 0) > now;
            return (
              <button
                key={action}
                type="button"
                className="claude-tama-care-btn"
                disabled={disabled}
                title={
                  petAsleep && action === 'feed'
                    ? 'Shh… Claude is sleeping 💤'
                    : (careCooldowns[action] || 0) > now
                      ? 'Claude needs a minute ⏳'
                      : petAsleep && action === 'play'
                        ? 'Sleepy rally! 💤🎾'
                        : label
                }
                onClick={(e) => {
                  // Don't bubble into the carousel tile's onClick.
                  e.stopPropagation();
                  if (!disabled) {
                    try { navigator.vibrate?.(8); } catch (err) { /* unsupported */ }
                    playSfx('press', { quiet: petAsleep });
                    // PLAY always starts the rally (was mood-gated at
                    // launch; user feedback: the game should always be
                    // playable). Asleep runs it in sleepy mode — slower
                    // ball, tighter window, half-effect server play. A win
                    // fires doCare('play'); at full mood the POST just has
                    // nothing to add.
                    if (action === 'play') startRally();
                    else doCare(action);
                  }
                }}
              >
                <span className="claude-tama-care-face">
                  <span style={{ fontSize: 16, lineHeight: 1 }}>{emoji}</span>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 8,
                      fontWeight: 800,
                      letterSpacing: 0.5,
                      color: disabled ? '#8d86a0' : '#241a3f',
                    }}
                  >
                    {label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      </div>
      </div>
      {/* 📱 Under-card ballot chip RETIRED 08-02 (Nina): the vote entry
          point moved into the top-right pill slot, replacing the AI
          Explorer badge on phones. (Original 08-01 rationale — in-strip
          spots collided with care buttons/vitals — was about the wide
          counted chip; the pill slot already hosted a same-size badge.) */}
    </div>
  );
};

export default ClaudeExplorerRailCard;
